/**
 * AnalyticsWorkbench — the full analytics/charting engine.
 * Extracted from the original monolithic Dashboard.tsx.
 * All auth, sidebar, and fin-page routing now live in App.tsx / AppShell.
 */
import { useState, useMemo, useCallback, useRef, useEffect, createContext, useContext } from "react"
import { loadAllData } from "@/data/loader"
import { useCurrency } from "@/context/CurrencyContext"
import type { DataRow as RealDataRow, GroupView, LoadedData } from "@/data/loader"
import { LANGUAGES, DIM_I18N, DIM_GROUP_I18N, VALUE_I18N, translateDataValue } from "@/i18n"
import type { Lang } from "@/i18n"
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts"
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
} from "@/components/ui/chart"
import type { ChartConfig } from "@/components/ui/chart"
import {
  ChevronRight, ChevronDown, ChevronLeft, FolderOpen, Folder, Settings2, Maximize2, Minimize2,
  X, Plus, BarChart3, TrendingUp, AreaChart as AreaChartIcon, PieChart as PieChartIcon,
  Layers, Download, FileSpreadsheet, Table2,
  Eye, Pencil, Lock, HelpCircle, FileDown, PlusCircle, LayoutGrid, Sliders,
  ArrowUpDown, ArrowUp, ArrowDown, ArrowDownAZ, ArrowUpAZ, Save, BookOpen, Trash2,
  AlignStartVertical, AlignEndVertical, TrendingDown,
  Sparkles, AlertTriangle, Activity, Users, DollarSign, Briefcase, Target,
  ArrowRight, ArrowUpRight, Shield, CalendarRange, Receipt, Building2, Bitcoin,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu"
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion"
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import html2canvas from "html2canvas"
import { jsPDF } from "jspdf"
import { useAuth } from "@/context/AuthContext"
import type { User, UserPerms } from "@/context/AuthContext"
import { ErrorBoundary } from "@/components/ErrorBoundary"

// AppMode and FinPage types are now in AppShell/FinSidebar

/* ═══════════════════════════════════════════════════════════
   DATA MODEL — loaded from /data/*.json (SQL fact table exports)
   Module-level mutable vars, populated by loadAllData() in App
   ═══════════════════════════════════════════════════════════ */
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
const QUARTERS = ["Q1","Q2","Q3","Q4"]

// Mutable data — set by loadAllData() before first meaningful render
let YEARS: string[] = []
let ENTITIES: string[] = []
/**
 * Whether the loaded view has a wallet book (transactions / fee revenue).
 *
 * The KPI row and the NL query defaults used to key off the entity button —
 * "if WWDA, show volume". A group view has no single answer to that, and
 * The Vault carries both a wallet book and a GL, so both read the data
 * instead. Recomputed on every load.
 */
let HAS_WALLET_BOOK = false
// dimension id → dataSource → fraction of rows with a real value. Populated
// by loadAllData(); see DimCoverage in loader.ts for why it exists.
let DIM_COVERAGE: Record<string, Record<string, number>> = {}

/**
 * Which of a panel's active data sources do NOT populate this dimension.
 *
 * A dimension below 5% coverage for a source is treated as inapplicable
 * rather than merely sparse: splitting by it collapses every row of that
 * source into one placeholder bucket, which reads as a real category on a
 * chart but carries no information.
 */
function incompatibleSources(dimId: string, activeSources: string[]): string[] {
  const cov = DIM_COVERAGE[dimId]
  if (!cov) return []
  return activeSources.filter(ds => cov[ds] !== undefined && cov[ds] < 0.05)
}
let VALUE_TREE: { id: string; label: string; children: { id: string; label: string }[] }[] = []
let SPLIT_TREE: { id: string; label: string; children: { id: string; label: string; values: string[] }[] }[] = []
let ALL_DIMS: { id: string; label: string; values: string[] }[] = []
let DIM_FIELD: Record<string, string> = {}
let FIELD_ORDER: Record<string, string[]> = {}

interface DataRow { [key: string]: any; year: string; quarter: string; month: string; yearMonth: string; entity: string; account: string; accountCode: string; scenario: string; dataSource: string; flowType: string; costType: string; counterparty: string; counterpartyType: string; clientName: string; currency: string; entrySide: string; contraCategory: string; value: number }
let DATA: DataRow[] = []
let _dataReady = false

// Color palette — blue-based multi-hue, matching CRM / shadcn chart style
// Uses distinct saturated colors so series are easily distinguishable
const PALETTE_LIGHT = [
  "#2563eb",   // blue-600
  "#38bdf8",   // sky-400
  "#6366f1",   // indigo-500
  "#8b5cf6",   // violet-500
  "#f59e0b",   // amber-500
  "#10b981",   // emerald-500
  "#f43f5e",   // rose-500
  "#06b6d4",   // cyan-500
  "#ec4899",   // pink-500
  "#84cc16",   // lime-500
  "#f97316",   // orange-500
  "#14b8a6",   // teal-500
]
const PALETTE_DARK = [
  "#60a5fa",   // blue-400
  "#38bdf8",   // sky-400
  "#818cf8",   // indigo-400
  "#a78bfa",   // violet-400
  "#fbbf24",   // amber-400
  "#34d399",   // emerald-400
  "#fb7185",   // rose-400
  "#22d3ee",   // cyan-400
  "#f472b6",   // pink-400
  "#a3e635",   // lime-400
  "#fb923c",   // orange-400
  "#2dd4bf",   // teal-400
]
// Area/line gradient fill opacity
const FILL_OPACITIES_LIGHT = [0.30, 0.25, 0.20, 0.18, 0.15, 0.12]
const FILL_OPACITIES_DARK  = [0.25, 0.20, 0.15, 0.12, 0.10, 0.08]
const BOTTOM_RATIO_LIGHT = 0.25
const BOTTOM_RATIO_DARK  = 0.10
// Bar gradient: top opacity and fade ratio
const BAR_OPACITIES_LIGHT = [0.85, 0.85, 0.85, 0.85, 0.85, 0.85]
const BAR_OPACITIES_DARK  = [0.80, 0.80, 0.80, 0.80, 0.80, 0.80]
const BAR_BOTTOM_RATIO_LIGHT = 0.70
const BAR_BOTTOM_RATIO_DARK  = 0.55

// Default — updated at render time by the theme
let PALETTE = PALETTE_LIGHT
let FILL_OPACITIES = FILL_OPACITIES_LIGHT
let BOTTOM_RATIO = BOTTOM_RATIO_LIGHT
let BAR_OPACITIES = BAR_OPACITIES_LIGHT
let BAR_BOTTOM = BAR_BOTTOM_RATIO_LIGHT

/* ═══ I18N CONTEXT ═══ */
const I18nContext = createContext<{ lang: Lang; t: (key: string) => string }>({ lang: "en", t: (k) => k })
const useI18n = () => useContext(I18nContext)

/** Get localized label for a dimension ID */
const dimLabel = (t: (k: string) => string, dimId: string, fallback?: string) => {
  const key = DIM_I18N[dimId]
  return key ? t(key) : fallback || dimId
}
/** Get localized label for a value item ID */
const valLabel = (t: (k: string) => string, valId: string) => {
  const key = VALUE_I18N[valId]
  if (key) return t(key)
  // Fallback: "v_feerevenue" → "Fee Revenue", "grp_cashflow" → "Cash Flow"
  const raw = valId.replace(/^(v_|grp_)/, "")
  return raw.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, c => c.toUpperCase()).replace(/([a-z])([a-z]+)/g, (_, first, rest) => first + rest)
}
/** Get localized label for a dimension group */
const groupLabel = (t: (k: string) => string, groupId: string, fallback: string) => {
  const key = DIM_GROUP_I18N[groupId]
  return key ? t(key) : fallback
}

/* ═══ AUTH (types imported from AuthContext) ═══ */
// The four hardcoded demo accounts that used to live here were removed in
// August 2026. Real users come from fin.users via /api/fin/users; the
// workbench's admin panel is a viewer over that, not a source of truth.

/* ═══ TYPES ═══ */
type ChartType = "bar" | "stackedBar" | "hbar" | "hbarStacked" | "line" | "area" | "pie" | "table"
type DimRole = "xaxis" | "filter" | "split"
type SortMode = "none" | "asc" | "desc" | "alpha_asc" | "alpha_desc"
type Magnitude = "auto" | "ones" | "thousands" | "millions" | "billions"
interface YAxisConfig { min?: number | "auto"; max?: number | "auto"; tickInterval?: number | "auto"; magnitude?: Magnitude; decimals?: number }
interface SplitConfig { role: DimRole; active: string[] }
interface PanelConfig { title: string; chartType: ChartType; selectedValues: string[]; activeSplits: Record<string, SplitConfig>; sortMode?: SortMode; yAxis?: YAxisConfig }
interface ChartData { data: Record<string, any>[]; series: string[]; xKey: string }
interface SavedTemplate { id: string; name: string; folder?: string; panel: PanelConfig; createdAt: string }

const TEMPLATES_KEY = "nf_chart_templates"
function loadTemplates(): SavedTemplate[] {
  try { const s = localStorage.getItem(TEMPLATES_KEY); return s ? JSON.parse(s) : [] } catch { return [] }
}
function saveTemplates(t: SavedTemplate[]) {
  try { localStorage.setItem(TEMPLATES_KEY, JSON.stringify(t)) } catch { /* quota */ }
}

// Built-in template folder categories — label is an i18n key
const TEMPLATE_FOLDERS = [
  { id: "revenue_pl", i18nKey: "folder.revenue_pl", icon: DollarSign },
  { id: "assets_aum", i18nKey: "folder.assets_aum", icon: Briefcase },
  { id: "clients", i18nKey: "folder.clients", icon: Users },
  { id: "costs", i18nKey: "folder.costs", icon: TrendingDown },
  { id: "custom", i18nKey: "folder.custom", icon: Folder },
] as const

type TemplateFolderID = typeof TEMPLATE_FOLDERS[number]["id"]

/**
 * Built-in templates — one saved chart each, rebuilt for whichever view is
 * loaded.
 *
 * Two rules they did not previously follow, both of which produced a template
 * that opened onto nothing:
 *
 *  - GATED ON MEASURED DATA. The list was fixed, so The Vault offered "AUM by
 *    service line" against a book holding no brokerage positions. A template
 *    that opens empty teaches people not to trust the menu. The gates are
 *    also what took the forecast out of here when INCLUDE_PLAN went off — no
 *    plan template had to be deleted, they simply stopped qualifying.
 *  - X-AXIS MEMBERS RANKED FROM ROWS. "Revenue by client" listed every client
 *    in the dimension — 247 of them in the All view, most with no revenue —
 *    which is unreadable and, per the same rule the presets follow, is not
 *    evidence of anything: the dim list is what the loader was told about.
 */
function makeBuiltinTemplates(t: (k: string) => string): SavedTemplate[] {
  const latestYear = YEARS.slice(-1)
  const lastTwoYears = YEARS.slice(-2)
  const present = new Set(DATA.map(r => r.dataSource))
  const flows = new Set(DATA.map(r => r.flowType))
  const has = (...s: string[]) => s.some(x => present.has(x))
  const entities = topNBy("d_entity", 12)
  const scenarios = dimValues("d_scenario")

  const isRevenue = (r: DataRow) => r.dataSource === "revenue"
  const isCost = (r: DataRow) => r.dataSource === "costs"
  const isFee = (r: DataRow) => r.flowType === "Fee Revenue"
  const rankClients = (n: number, where: (r: DataRow) => boolean) =>
    topNBy("d_clientName", n, r => isRealClientName(r.clientName) && where(r))

  const out: SavedTemplate[] = []
  const add = (id: string, folder: TemplateFolderID, key: string, panel: PanelConfig) =>
    out.push({ id, name: t(key), folder, createdAt: "2024-01-01T00:00:00Z", panel })

  /* ── Revenue & P&L ──────────────────────────────────────── */
  if (has("revenue", "costs")) {
    add("builtin_rev_vs_exp", "revenue_pl", "panel.revVsExpenses",
      makePanelConfig(t("panel.revVsExpenses"), ["v_cashflow"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_dataSource: { role: "split", active: ["costs", "revenue"].filter(s => present.has(s)) },
        d_year: { role: "filter", active: [...lastTwoYears] },
      }, "bar"))
    add("builtin_exp_rev_cat", "revenue_pl", "panel.expRevByCategory",
      makePanelConfig(t("panel.expRevByCategory"), ["v_cashflow"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_account: { role: "split", active: topNBy("d_account", 12, r => isRevenue(r) || isCost(r)) },
        d_year: { role: "filter", active: [...lastTwoYears] },
      }, "stackedBar"))
  }
  if (has("net") && entities.length > 1) {
    add("builtin_net_by_entity", "revenue_pl", "panel.p.netByEntity",
      makePanelConfig(t("panel.p.netByEntity"), ["v_cashflow"], {
        d_entity: { role: "xaxis", active: entities },
        d_dataSource: { role: "filter", active: ["net"] },
        d_year: { role: "filter", active: [...latestYear] },
      }, "hbar", "desc"))
  }
  // Scenario on Split, never summed — the one safe way to put plan and actual
  // on the same chart.
  if (scenarios.includes("Plan") && scenarios.includes("Actual")) {
    add("builtin_plan_actual", "revenue_pl", "panel.p.planVsActualMonth",
      makePanelConfig(t("panel.p.planVsActualMonth"), ["v_cashflow"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_scenario: { role: "split", active: ["Plan", "Actual"] },
        d_year: { role: "filter", active: [...latestYear] },
      }, "bar"))
  }

  /* ── Assets & AUM ───────────────────────────────────────── */
  if (flows.has("Assets Held")) {
    add("builtin_aum_service", "assets_aum", "panel.aumServiceMonthly",
      makePanelConfig(t("panel.aumServiceMonthly"), ["v_assets"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_serviceType: { role: "split", active: dimValues("d_serviceType") },
        d_year: { role: "filter", active: [...lastTwoYears] },
      }, "stackedBar"))
  }
  if (flows.has("Balance")) {
    // Both books report a "Balance" flow and they are not the same thing: the
    // wallet's are client crypto balances by product, WWPI's are the entity's
    // own treasury by GL account. Same panel shape, different titles, because
    // calling a bank's treasury "wallet balances" is how a chart gets read as
    // the wrong number.
    const isWallet = flows.has("Transaction Volume") || has("fees")
    const key = isWallet ? "panel.p.walletBalanceProduct" : "panel.treasuryBalanceMonthly"
    add("builtin_balances_product", "assets_aum", key,
      makePanelConfig(t(key), ["v_balance"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_account: { role: "split", active: topNBy("d_account", 10, r => r.flowType === "Balance") },
        d_year: { role: "filter", active: [...latestYear] },
      }, "area"))
  }

  /* ── Clients ────────────────────────────────────────────── */
  const revClients = rankClients(25, isRevenue)
  if (revClients.length) {
    add("builtin_rev_client", "clients", "panel.revByClient",
      makePanelConfig(t("panel.revByClient"), ["v_cashflow"], {
        d_clientName: { role: "xaxis", active: revClients },
        d_year: { role: "filter", active: [...latestYear] },
        d_dataSource: { role: "filter", active: ["revenue"] },
        d_account: { role: "split", active: topNBy("d_account", 8, isRevenue) },
      }, "stackedBar", "desc"))
    if (has("costs")) {
      add("builtin_client_profitability", "clients", "panel.clientRevVsCosts",
        makePanelConfig(t("panel.clientRevVsCosts"), ["v_cashflow"], {
          d_clientName: { role: "xaxis", active: revClients },
          d_dataSource: { role: "split", active: ["revenue", "costs"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "bar", "desc"))
    }
  }
  const feeClients = rankClients(25, isFee)
  if (feeClients.length) {
    add("builtin_fee_client", "clients", "panel.p.top10ClientsFees",
      makePanelConfig(t("panel.p.top10ClientsFees"), ["v_feerevenue"], {
        d_clientName: { role: "xaxis", active: feeClients },
        d_year: { role: "filter", active: latestYearWith(isFee) },
      }, "hbar", "desc"))
  }

  /* ── Cost analysis ──────────────────────────────────────── */
  if (has("costs")) {
    add("builtin_cost_structure", "costs", "panel.costStructYearly",
      makePanelConfig(t("panel.costStructYearly"), ["v_cashflow"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_costType: { role: "split", active: dimValues("d_costType") },
        d_dataSource: { role: "filter", active: ["costs"] },
        d_year: { role: "filter", active: [...lastTwoYears] },
      }, "stackedBar"))
    if (entities.length > 1) {
      add("builtin_cost_by_entity", "costs", "panel.p.costByEntity",
        makePanelConfig(t("panel.p.costByEntity"), ["v_cashflow"], {
          d_entity: { role: "xaxis", active: entities },
          d_dataSource: { role: "filter", active: ["costs"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc"))
    }
  }
  if (has("jira_invoices")) {
    const isAP = (r: DataRow) => r.dataSource === "jira_invoices"
    add("builtin_top_vendors", "costs", "panel.p.topVendors",
      makePanelConfig(t("panel.p.topVendors"), ["v_cashflow"], {
        d_counterparty: { role: "xaxis", active: topNBy("d_counterparty", 15, isAP) },
        d_dataSource: { role: "filter", active: ["jira_invoices"] },
        d_year: { role: "filter", active: latestYearWith(isAP) },
      }, "hbar", "desc"))
  }

  // A small view — TFF is one company with 24 rows of plan payroll — passes
  // none of the gates above and would open an empty template list, which
  // reads as broken rather than as small. One chart of what it does have.
  if (!out.length && DATA.length) {
    add("builtin_total_month", "revenue_pl", "panel.p.totalByMonth",
      makePanelConfig(t("panel.p.totalByMonth"), [VALUE_TREE[0]?.children[0]?.id || "v_cashflow"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_dataSource: { role: "split", active: dimValues("d_dataSource") },
        d_year: { role: "filter", active: [...latestYear] },
      }, "bar"))
  }

  return out
}

function makePanelConfig(title: string, selectedValues: string[], splits: Record<string, { role: string; active: string[] }>, chartType: ChartType, sortMode?: string): PanelConfig {
  const activeSplits: Record<string, SplitConfig> = {}
  for (const [k, v] of Object.entries(splits)) {
    activeSplits[k] = { role: v.role as "xaxis" | "split" | "filter", active: v.active }
  }
  return { title, chartType, selectedValues, activeSplits, sortMode: (sortMode || "none") as any }
}

const CHART_TYPES: { id: ChartType; i18nKey: string; Icon: any }[] = [
  { id: "bar", i18nKey: "chartType.bar", Icon: BarChart3 },
  { id: "stackedBar", i18nKey: "chartType.stackedBar", Icon: Layers },
  { id: "hbar", i18nKey: "chartType.hbar", Icon: AlignStartVertical },
  { id: "hbarStacked", i18nKey: "chartType.hbarStacked", Icon: AlignEndVertical },
  { id: "line", i18nKey: "chartType.line", Icon: TrendingUp },
  { id: "area", i18nKey: "chartType.area", Icon: AreaChartIcon },
  { id: "pie", i18nKey: "chartType.pie", Icon: PieChartIcon },
  { id: "table", i18nKey: "chartType.table", Icon: Table2 },
]

/** Expandable chart type picker — shows current icon, expands on hover to show all options */
const ChartTypePicker = ({ value, onChange, disabled }: { value: ChartType; onChange: (v: ChartType) => void; disabled?: boolean }) => {
  const [open, setOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null)
  const current = CHART_TYPES.find(c => c.id === value) || CHART_TYPES[0]
  const handleEnter = () => { if (timerRef.current) clearTimeout(timerRef.current); setOpen(true) }
  const handleLeave = () => { timerRef.current = setTimeout(() => setOpen(false), 200) }
  return (
    <div className="relative flex items-center" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        className="h-6 flex items-center justify-center rounded hover:bg-accent text-muted-foreground gap-0">
        <ChevronLeft className={`h-2.5 w-2.5 opacity-40 transition-transform ${open ? "rotate-0" : "rotate-0"}`} />
        <current.Icon className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute top-0 right-0 z-20 flex items-center gap-0.5 bg-popover border rounded-md shadow-md p-0.5"
          onClick={e => e.stopPropagation()}>
          {CHART_TYPES.map(({ id, Icon }) => (
            <button key={id} onClick={() => { if (!disabled) { onChange(id); setOpen(false) } }}
              className={`h-6 w-6 flex items-center justify-center rounded transition-colors ${value === id ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50"}`}>
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const fmt = (v: number | null | undefined) => {
  if (v == null) return "—"
  const a = Math.abs(v)
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`
  return Math.round(v).toLocaleString()
}
const fmtY = (v: number | null | undefined, mag: Magnitude = "auto", dec = 0): string => {
  if (v == null) return "—"
  if (mag === "auto") return fmt(v)
  const divisors: Record<string, [number, string]> = { ones: [1, ""], thousands: [1e3, "K"], millions: [1e6, "M"], billions: [1e9, "B"] }
  const [d, suffix] = divisors[mag] || [1, ""]
  return (v / d).toFixed(dec) + suffix
}

const makePanel = (title: string, vals: string[], splits: Record<string, SplitConfig>, ct: ChartType = "bar", sort: SortMode = "none", yAxis?: YAxisConfig): PanelConfig => ({
  title, chartType: ct, selectedValues: vals, activeSplits: splits, sortMode: sort, ...(yAxis ? { yAxis } : {}),
})
/* ═══════════════════════════════════════════════════════════
   GROUP-VIEW DEFAULTS

   One builder for all three views. It used to be four — one per
   legal entity — which only worked because each entity had
   exactly one shape of data: WWF had client AUM, TB and WWPI had
   a GL and no clients, WWDA had wallet fees and no costs.

   A group spans several of those shapes at once (The Vault is
   Tria Bridge's GL *and* the wallet feeds), so the panels are
   chosen by what the loaded rows actually carry rather than by
   which button was pressed. A panel whose data is absent is
   dropped instead of rendering an empty chart.
   ═══════════════════════════════════════════════════════════ */
function makeGroupDefaultPanels(t: (k: string) => string): PanelConfig[] {
  const latestYear = YEARS.slice(-1)
  const lastTwoYears = YEARS.slice(-2)
  const dimValues = (id: string) => ALL_DIMS.find(d => d.id === id)?.values || []
  const sources = new Set(dimValues("d_dataSource"))
  const flows = new Set(DATA.map(r => r.flowType))

  const hasClients = dimValues("d_clientName").filter(isRealClientName).length > 1
  /** Biggest real clients by measured total, not by position in the dim list. */
  const topClients = (n: number, where: (r: DataRow) => boolean) =>
    topNBy("d_clientName", n, r => isRealClientName(r.clientName) && where(r))

  const entities = [...ENTITIES]
  const panels: (PanelConfig | null)[] = []

  // 1. The group's own P&L shape: revenue, costs and the synthesized net line.
  if (sources.has("revenue") && sources.has("costs")) {
    panels.push(makePanel(t("panel.cashFlowWithNet"), ["v_cashflow"], {
      d_month: { role: "xaxis", active: [...MONTHS] },
      d_dataSource: { role: "split", active: ["revenue", "costs", "net"].filter(s => sources.has(s)) },
      d_year: { role: "filter", active: [...lastTwoYears] },
    }, "line"))
  }

  // 2. Who inside the group the money is running through. This is the panel
  //    the group views exist for — rank by measured total so the largest
  //    entities are the visible series.
  if (entities.length > 1) {
    panels.push(makePanel(t("panel.revByEntityYearly"), ["v_cashflow", "v_feerevenue"], {
      d_month: { role: "xaxis", active: [...MONTHS] },
      d_entity: { role: "split", active: topNBy("d_entity", 12) },
      d_year: { role: "filter", active: [...lastTwoYears] },
    }, "bar"))
  }

  // 3. Client concentration, from whichever book the group actually keeps —
  //    brokerage positions for WWG, wallet balances for The Vault.
  if (hasClients && flows.has("Assets Held")) {
    panels.push(makePanel(t("panel.aumByClient"), ["v_assets"], {
      d_clientName: { role: "xaxis", active: topClients(25, r => r.flowType === "Assets Held") },
      d_serviceType: { role: "split", active: dimValues("d_serviceType") },
      d_year: { role: "filter", active: latestYear },
    }, "stackedBar", "desc"))
  } else if (hasClients && flows.has("Balance")) {
    panels.push(makePanel(t("panel.walletBalanceByClient"), ["v_balance"], {
      d_clientName: { role: "xaxis", active: topClients(25, r => r.flowType === "Balance") },
      d_account: { role: "split", active: dimValues("d_balancesCategory") },
      d_year: { role: "filter", active: latestYear },
    }, "stackedBar", "desc"))
  }

  // 4. Where the revenue comes from month to month — by client for a book
  //    that has them, by fee type for the wallet.
  if (hasClients && sources.has("revenue")) {
    panels.push(makePanel(t("panel.revByMonthByClient"), ["v_cashflow"], {
      d_month: { role: "xaxis", active: [...MONTHS] },
      d_clientName: { role: "split", active: topClients(12, r => r.dataSource === "revenue") },
      d_dataSource: { role: "filter", active: ["revenue"] },
      d_year: { role: "filter", active: latestYear },
    }, "stackedBar"))
  }
  if (flows.has("Fee Revenue")) {
    panels.push(makePanel(t("panel.feeRevMonthlyTrend"), ["v_feerevenue"], {
      d_month: { role: "xaxis", active: [...MONTHS] },
      d_year: { role: "split", active: [...lastTwoYears] },
    }, "line"))
    panels.push(makePanel(t("panel.feeStructMonthly"), ["v_feerevenue"], {
      d_month: { role: "xaxis", active: [...MONTHS] },
      d_account: { role: "split", active: dimValues("d_feesCategory") },
      d_year: { role: "filter", active: [...lastTwoYears] },
    }, "stackedBar"))
  }

  // 5. Cost structure. Actual costs only — the plan sources carry their own
  //    scenario and adding them here would sum forecast onto history.
  if (sources.has("costs")) {
    panels.push(makePanel(t("panel.costStructMonthly"), ["v_cashflow"], {
      d_month: { role: "xaxis", active: [...MONTHS] },
      d_costType: { role: "split", active: dimValues("d_costType") },
      d_dataSource: { role: "filter", active: ["costs"] },
      d_year: { role: "filter", active: [...lastTwoYears] },
    }, "stackedBar"))
  }

  const built = panels.filter(Boolean) as PanelConfig[]
  // A view with no recognisable shape still gets something to look at rather
  // than an empty dashboard.
  if (!built.length) {
    built.push(makePanel(t("panel.dataSourceBreakdown"), ["v_cashflow", "v_transactionvolume", "v_feerevenue", "v_balance"], {
      d_dataSource: { role: "xaxis", active: dimValues("d_dataSource") },
      d_year: { role: "filter", active: [...lastTwoYears] },
    }, "bar", "desc"))
  }
  return built
}
let DEFAULT_PANELS: PanelConfig[] = []

/* ═══════════════════════════════════════════════════════════
   DASHBOARD PRESETS — pre-built multi-panel views.

   A preset is a named set of panels that replaces the dashboard
   tab's contents. Unlike a saved template (one panel), a preset
   is a whole readable dashboard on one theme.

   Every builder runs AFTER loadAllData() has populated the
   module-level DATA/ALL_DIMS/DIM_FIELD, so they can rank real
   values instead of guessing. Builders must degrade gracefully:
   dims like division/vendor only exist for some entities, so a
   panel whose dim is missing returns null and is dropped.
   ═══════════════════════════════════════════════════════════ */

/** Values of a dim, or [] when the dim isn't present for this entity. */
const dimValues = (dimId: string): string[] => ALL_DIMS.find(d => d.id === dimId)?.values || []

/**
 * Is this client name a client?
 *
 * The wallet client list carries internal accounts, treasury buckets and test
 * users alongside real ones. They are real rows and belong in totals, but on
 * a "top clients" chart they crowd out the people the chart is about.
 * Shared by the presets, the templates and the default panels so all three
 * agree on who counts.
 */
const CLIENT_SKIP = ["(", "Internal|", "Invest|", "Transit", "Reserves", "Fees", "CARDS", "Loans",
                     "login", "test", "demo", "user", "bitcoin", "ripple", "solana", "tron", "bot ",
                     "olegtst", "nikolich"]
const isRealClientName = (v: string): boolean =>
  !!v && v !== "(none)" && v !== "(unknown)" &&
  !CLIENT_SKIP.some(p => v.toLowerCase().startsWith(p.toLowerCase()))

/**
 * Rank a dim's values by summed magnitude, biggest first.
 *
 * The older panels approximated "top clients" with
 * `realClients.slice(0, 20)` — that slices the dim list in load order, so
 * the largest client can fall outside the slice entirely and the chart
 * silently lies. This sums the actual rows instead.
 */
function topNBy(dimId: string, n: number, where?: (r: DataRow) => boolean): string[] {
  const field = DIM_FIELD[dimId]
  if (!field || !DATA.length) return []
  const totals = new Map<string, number>()
  for (const r of DATA) {
    if (where && !where(r)) continue
    const key = String(r[field] ?? "")
    if (!key || key.startsWith("(")) continue
    // Magnitude, not signed value — costs are booked negative in some
    // sources, and a ranking wants size regardless of direction.
    totals.set(key, (totals.get(key) || 0) + Math.abs(Number(r.value) || 0))
  }
  return [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, n).map(([k]) => k)
}

/**
 * Latest year that actually carries rows matching `where`.
 *
 * Sources do not all run to today: WWF's supplier invoices effectively stop
 * in 2023, so pinning those panels to the newest year overall rendered two
 * invoices and read as "we spent nothing" rather than "this feed ended".
 */
function latestYearWith(where: (r: DataRow) => boolean): string[] {
  const years = new Set<string>()
  for (const r of DATA) if (where(r) && r.year) years.add(String(r.year))
  if (!years.size) return []
  return [[...years].sort().pop() as string]
}

interface DashboardPreset {
  id: string
  nameKey: string
  Icon: any
  panels: PanelConfig[]
}

/**
 * Pin the y-axis to one unit so ticks don't flip between "900K" and "1.2M"
 * down a single axis. `fmt()` already abbreviates; this just keeps it
 * consistent within a panel.
 */
const EUR_K: YAxisConfig = { magnitude: "thousands", decimals: 0 }

function makeDashboardPresets(t: (k: string) => string): DashboardPreset[] {
  const latestYear = YEARS.slice(-1)
  const lastTwoYears = YEARS.slice(-2)
  const costTypes = dimValues("d_costType")
  const isRevenue = (r: DataRow) => r.dataSource === "revenue"
  const presets: DashboardPreset[] = []

  // Measured, not declared. `d_dataSource`'s value list is the source list
  // buildLoadedData() was *told* about, so it names jira_invoices and
  // payroll_actual for WWF whether or not those queries returned rows.
  // Scanning the rows is the only honest test, and it keeps a preset from
  // offering itself as four empty charts.
  const present = new Set(DATA.map(r => r.dataSource))
  const hasSource = (...sources: string[]) => sources.some(s => present.has(s))
  const flows = new Set(DATA.map(r => r.flowType))

  // A group view is several companies; a single-company view is one. Panels
  // that slice by company are the reason the toggle is a group at all, so
  // they are built whenever there is more than one to compare — and skipped
  // rather than drawn as a single bar when there is not.
  const entities = topNBy("d_entity", 12)
  const multiEntity = entities.length > 1
  const jurisdictions = topNBy("d_jurisdiction", 8)

  /** Clients worth charting, ranked from rows and free of internal accounts. */
  const rankClients = (n: number, where: (r: DataRow) => boolean) =>
    topNBy("d_clientName", n, r => isRealClientName(r.clientName) && where(r))

  /* ── A · Year in Review ─────────────────────────────────── */
  const yearInReview: PanelConfig[] = [
    // Net is a synthetic dataSource from loader.synthesizeNetPL — a line
    // keeps it legible against the revenue/cost bars-of-thought.
    makePanel(t("panel.p.revCostNetMonth"), ["v_cashflow"], {
      d_month: { role: "xaxis", active: [...MONTHS] },
      d_dataSource: { role: "split", active: ["revenue", "costs", "net"] },
      d_year: { role: "filter", active: [...latestYear] },
    }, "line", "none", EUR_K),
    makePanel(t("panel.p.netByQuarter"), ["v_cashflow"], {
      d_quarter: { role: "xaxis", active: [...QUARTERS] },
      d_year: { role: "split", active: [...lastTwoYears] },
      d_dataSource: { role: "filter", active: ["net"] },
    }, "bar", "none", EUR_K),
    makePanel(t("panel.p.costStructYtd"), ["v_cashflow"], {
      d_month: { role: "xaxis", active: [...MONTHS] },
      d_costType: { role: "split", active: costTypes },
      d_dataSource: { role: "filter", active: ["costs"] },
      d_year: { role: "filter", active: [...latestYear] },
    }, "stackedBar", "none", EUR_K),
    // Horizontal — category names are long and unreadable rotated.
    makePanel(t("panel.p.revByCategory"), ["v_cashflow"], {
      d_account: { role: "xaxis", active: topNBy("d_account", 12, isRevenue) },
      d_dataSource: { role: "filter", active: ["revenue"] },
      d_year: { role: "filter", active: [...latestYear] },
    }, "hbar", "desc", EUR_K),
  ]
  if (hasSource("revenue", "costs")) {
    presets.push({ id: "year_in_review", nameKey: "preset.yearInReview", Icon: CalendarRange, panels: yearInReview })
  }

  /* ── A2 · By Company ─────────────────────────────────────────
     The preset a group view exists for. Everything here puts the
     Entity dimension on a shelf, which is also what keeps it out
     of the plan/actual and payroll double-count traps: the
     sources stay separate series rather than one total. */
  if (multiEntity) {
    const byCompany: PanelConfig[] = []
    if (hasSource("revenue", "costs")) {
      byCompany.push(
        makePanel(t("panel.p.revCostByEntity"), ["v_cashflow"], {
          d_entity: { role: "xaxis", active: entities },
          d_dataSource: { role: "split", active: ["revenue", "costs"].filter(s => present.has(s)) },
          d_year: { role: "filter", active: [...latestYear] },
        }, "bar", "desc", EUR_K),
      )
    }
    if (hasSource("net")) {
      byCompany.push(
        makePanel(t("panel.p.netByEntity"), ["v_cashflow"], {
          d_entity: { role: "xaxis", active: entities },
          d_dataSource: { role: "filter", active: ["net"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc", EUR_K),
      )
    }
    if (hasSource("revenue")) {
      byCompany.push(
        makePanel(t("panel.p.revByEntityMonth"), ["v_cashflow"], {
          d_month: { role: "xaxis", active: [...MONTHS] },
          d_entity: { role: "split", active: entities },
          d_dataSource: { role: "filter", active: ["revenue"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "stackedBar", "none", EUR_K),
      )
    }
    // Aggregate by company, never by employee — payroll_actual's counterparty
    // is a person's name. A company total is the same rule as division/team.
    if (hasSource("payroll_actual")) {
      byCompany.push(
        makePanel(t("panel.p.payrollByEntity"), ["v_cashflow"], {
          d_entity: { role: "xaxis", active: entities },
          d_dataSource: { role: "filter", active: ["payroll_actual"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc", EUR_K),
      )
    }
    if (jurisdictions.length > 1 && hasSource("revenue", "costs")) {
      byCompany.push(
        makePanel(t("panel.p.byJurisdiction"), ["v_cashflow"], {
          d_jurisdiction: { role: "xaxis", active: jurisdictions },
          d_dataSource: { role: "split", active: ["revenue", "costs"].filter(s => present.has(s)) },
          d_year: { role: "filter", active: [...latestYear] },
        }, "bar", "desc", EUR_K),
      )
    }
    if (byCompany.length) {
      presets.push({ id: "by_company", nameKey: "preset.byCompany", Icon: Building2, panels: byCompany })
    }
  }

  /* ── A3 · Plan vs Actual ─────────────────────────────────────
     Scenario goes on Split or the X axis, never left to sum: that
     is the difference between comparing forecast with history and
     adding them together. The panel warning stays quiet for
     exactly this reason. */
  const scenarios = dimValues("d_scenario")
  if (scenarios.includes("Plan") && scenarios.includes("Actual")) {
    const planActual: PanelConfig[] = [
      makePanel(t("panel.p.planVsActualMonth"), ["v_cashflow"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_scenario: { role: "split", active: ["Plan", "Actual"] },
        d_year: { role: "filter", active: [...latestYear] },
      }, "bar", "none", EUR_K),
    ]
    if (multiEntity) {
      planActual.push(
        makePanel(t("panel.p.planVsActualEntity"), ["v_cashflow"], {
          d_entity: { role: "xaxis", active: entities },
          d_scenario: { role: "split", active: ["Plan", "Actual"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "bar", "desc", EUR_K),
      )
    }
    planActual.push(
      makePanel(t("panel.p.planVsActualQuarter"), ["v_cashflow"], {
        d_quarter: { role: "xaxis", active: [...QUARTERS] },
        d_scenario: { role: "split", active: ["Plan", "Actual"] },
        d_year: { role: "filter", active: [...latestYear] },
      }, "bar", "none", EUR_K),
    )
    presets.push({ id: "plan_actual", nameKey: "preset.planActual", Icon: Target, panels: planActual })
  }

  /* ── B · Top 10 Clients ─────────────────────────────────────
     Ranked from GL revenue where a group has a revenue book, and
     from fees where its income is the wallet's instead — The
     Vault's clients are real and were invisible to a preset that
     only knew about `revenue`. */
  const top10 = rankClients(10, isRevenue)
  if (top10.length) {
    presets.push({
      id: "top_clients", nameKey: "preset.topClients", Icon: Users, panels: [
        makePanel(t("panel.p.top10ClientsRev"), ["v_cashflow"], {
          d_clientName: { role: "xaxis", active: top10 },
          d_dataSource: { role: "filter", active: ["revenue"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc", EUR_K),
        makePanel(t("panel.p.top10ClientsTrend"), ["v_cashflow"], {
          d_month: { role: "xaxis", active: [...MONTHS] },
          d_clientName: { role: "split", active: top10 },
          d_dataSource: { role: "filter", active: ["revenue"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "stackedBar", "none", EUR_K),
        makePanel(t("panel.p.clientConcentration"), ["v_cashflow"], {
          d_clientName: { role: "xaxis", active: top10 },
          d_dataSource: { role: "filter", active: ["revenue"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "pie", "desc"),
        makePanel(t("panel.p.top10RevVsCost"), ["v_cashflow"], {
          d_clientName: { role: "xaxis", active: top10 },
          d_dataSource: { role: "split", active: ["revenue", "costs"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc", EUR_K),
      ],
    })
  } else if (flows.has("Fee Revenue")) {
    // No GL revenue book, but a wallet that earns fees from named clients.
    const isFee = (r: DataRow) => r.flowType === "Fee Revenue"
    const topFee = rankClients(10, isFee)
    const feeYear = latestYearWith(isFee)
    if (topFee.length && feeYear.length) {
      presets.push({
        id: "top_clients", nameKey: "preset.topClientsFees", Icon: Users, panels: [
          makePanel(t("panel.p.top10ClientsFees"), ["v_feerevenue"], {
            d_clientName: { role: "xaxis", active: topFee },
            d_year: { role: "filter", active: [...feeYear] },
          }, "hbar", "desc", EUR_K),
          makePanel(t("panel.p.top10FeesTrend"), ["v_feerevenue"], {
            d_month: { role: "xaxis", active: [...MONTHS] },
            d_clientName: { role: "split", active: topFee },
            d_year: { role: "filter", active: [...feeYear] },
          }, "stackedBar", "none", EUR_K),
          makePanel(t("panel.p.clientConcentration"), ["v_feerevenue"], {
            d_clientName: { role: "xaxis", active: topFee },
            d_year: { role: "filter", active: [...feeYear] },
          }, "pie", "desc"),
        ],
      })
    }
  }

  /* ── B2 · Wallet & Transactions ──────────────────────────────
     The Vault's actual business, and until now no preset touched
     it: 12,799 transaction rows and 1,259 fee rows sat behind a
     dashboard built entirely from a general ledger. Volume is a
     throughput measure and is deliberately kept on its own panel
     rather than added to anything. */
  if (flows.has("Transaction Volume") || flows.has("Fee Revenue")) {
    const walletPanels: PanelConfig[] = []
    const isTx = (r: DataRow) => r.flowType === "Transaction Volume"
    const txYear = latestYearWith(isTx)
    if (flows.has("Transaction Volume") && txYear.length) {
      walletPanels.push(
        makePanel(t("panel.p.txVolumeMonth"), ["v_transactionvolume"], {
          d_month: { role: "xaxis", active: [...MONTHS] },
          d_year: { role: "split", active: [...lastTwoYears] },
        }, "line", "none", EUR_K),
        makePanel(t("panel.p.txByCategory"), ["v_transactionvolume"], {
          d_account: { role: "xaxis", active: topNBy("d_account", 12, isTx) },
          d_year: { role: "filter", active: [...txYear] },
        }, "hbar", "desc", EUR_K),
      )
    }
    if (flows.has("Fee Revenue")) {
      const isFee = (r: DataRow) => r.flowType === "Fee Revenue"
      const feeYear = latestYearWith(isFee)
      if (feeYear.length) {
        walletPanels.push(
          makePanel(t("panel.p.feeByType"), ["v_feerevenue"], {
            d_month: { role: "xaxis", active: [...MONTHS] },
            d_account: { role: "split", active: topNBy("d_account", 10, isFee) },
            d_year: { role: "filter", active: [...feeYear] },
          }, "stackedBar", "none", EUR_K),
        )
      }
    }
    if (flows.has("Balance")) {
      const isBal = (r: DataRow) => r.flowType === "Balance"
      const balYear = latestYearWith(isBal)
      if (balYear.length) {
        walletPanels.push(
          makePanel(t("panel.p.walletBalanceProduct"), ["v_balance"], {
            d_month: { role: "xaxis", active: [...MONTHS] },
            d_account: { role: "split", active: topNBy("d_account", 10, isBal) },
            d_year: { role: "filter", active: [...balYear] },
          }, "area", "none", EUR_K),
        )
      }
    }
    if (walletPanels.length) {
      presets.push({ id: "wallet", nameKey: "preset.wallet", Icon: Bitcoin, panels: walletPanels })
    }
  }

  /* ── C · Payroll Analytics ──────────────────────────────────
     Deliberately aggregate-only. `payroll_actual` carries
     employee_name as its counterparty, so an employee-level panel
     would put individual salaries on a shared dashboard. Group by
     division/team/position instead.
     Actual (payroll_actual) and Plan (budget_payroll) are kept in
     separate panels — the org dims only exist on the plan side, and
     mixing them would present modelled figures as measured ones. */
  const payrollPanels: PanelConfig[] = []
  if (hasSource("payroll_actual")) {
    payrollPanels.push(
      makePanel(t("panel.p.payrollMonthYoY"), ["v_cashflow"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_year: { role: "split", active: [...lastTwoYears] },
        d_dataSource: { role: "filter", active: ["payroll_actual"] },
      }, "bar", "none", EUR_K),
    )
    // By company is the only other breakdown the actuals can carry: their
    // rows hold an employee name and nothing else organisational, and an
    // employee-level payroll chart is exactly what this preset will not do.
    // Division, team and position live on the plan side only.
    if (multiEntity) {
      payrollPanels.push(
        makePanel(t("panel.p.payrollByEntity"), ["v_cashflow"], {
          d_entity: { role: "xaxis", active: entities },
          d_dataSource: { role: "filter", active: ["payroll_actual"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc", EUR_K),
      )
      payrollPanels.push(
        makePanel(t("panel.p.payrollEntityMonth"), ["v_cashflow"], {
          d_month: { role: "xaxis", active: [...MONTHS] },
          d_entity: { role: "split", active: entities },
          d_dataSource: { role: "filter", active: ["payroll_actual"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "stackedBar", "none", EUR_K),
      )
    }
  }
  if (hasSource("budget_payroll")) {
    // Ranked from budget_payroll rows specifically: d_division is also
    // populated by budget_expense, so the dim can be non-empty while the
    // payroll side of it has nothing to plot.
    const isPlanPayroll = (r: DataRow) => r.dataSource === "budget_payroll"
    const divisions = topNBy("d_division", 8, isPlanPayroll)
    if (divisions.length > 1) {
      payrollPanels.push(
        makePanel(t("panel.p.payrollByDivision"), ["v_cashflow"], {
          d_month: { role: "xaxis", active: [...MONTHS] },
          d_division: { role: "split", active: divisions },
          d_dataSource: { role: "filter", active: ["budget_payroll"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "stackedBar", "none", EUR_K),
      )
    }
    const topTeams = topNBy("d_team", 12, isPlanPayroll)
    if (topTeams.length > 1) {
      payrollPanels.push(
        makePanel(t("panel.p.payrollByTeam"), ["v_cashflow"], {
          d_team: { role: "xaxis", active: topTeams },
          d_dataSource: { role: "filter", active: ["budget_payroll"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc", EUR_K),
      )
    }
    // Deliberately NOT a "payroll vs other costs" panel. `payroll_actual` is
    // a per-employee breakdown of the same money 1C already books to account
    // 800300 PAYROLL EXPENSES inside the `costs` source — month by month the
    // two agree to within a rounding error — so charting the pair together
    // would show payroll twice and overstate total costs by about half.
    const topPositions = topNBy("d_position", 12, isPlanPayroll)
    if (topPositions.length > 1) {
      payrollPanels.push(
        makePanel(t("panel.p.payrollByPosition"), ["v_cashflow"], {
          d_position: { role: "xaxis", active: topPositions },
          d_dataSource: { role: "filter", active: ["budget_payroll"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc", EUR_K),
      )
    }
  }
  if (payrollPanels.length) {
    presets.push({ id: "payroll", nameKey: "preset.payroll", Icon: Briefcase, panels: payrollPanels })
  }

  /* ── D · Cost Control & AP ──────────────────────────────── */
  const costPanels: PanelConfig[] = []
  const isAP = (r: DataRow) => r.dataSource === "jira_invoices"
  // The AP feed does not run to the present day for every entity, so these
  // panels follow the invoices rather than the calendar.
  const apYear = latestYearWith(isAP)
  if (hasSource("jira_invoices") && apYear.length) {
    const topVendors = topNBy("d_counterparty", 15, isAP)
    if (topVendors.length) {
      costPanels.push(
        makePanel(t("panel.p.topVendors"), ["v_cashflow"], {
          d_counterparty: { role: "xaxis", active: topVendors },
          d_dataSource: { role: "filter", active: ["jira_invoices"] },
          d_year: { role: "filter", active: [...apYear] },
        }, "hbar", "desc", EUR_K),
      )
    }
    costPanels.push(
      makePanel(t("panel.p.directVsOwn"), ["v_cashflow"], {
        d_month: { role: "xaxis", active: [...MONTHS] },
        d_costType: { role: "split", active: ["Direct", "Own expense"] },
        d_dataSource: { role: "filter", active: ["jira_invoices"] },
        d_year: { role: "filter", active: [...apYear] },
      }, "stackedBar", "none", EUR_K),
    )
    // contraCategory carries the AP workflow stage for Jira invoices:
    // Paid / Approved for payment / In approval / New (unprocessed). It must
    // be ranked from the AP rows rather than taken from the whole dim — the
    // same field holds 1C contra categories, and computeData seeds the x-axis
    // from FIELD_ORDER ∩ active, so unrelated members would render as a row
    // of empty bars.
    const apStages = topNBy("d_contraCategory", 6, isAP)
    if (apStages.length > 1) {
      costPanels.push(
        makePanel(t("panel.p.apPipeline"), ["v_cashflow"], {
          d_contraCategory: { role: "xaxis", active: apStages },
          d_dataSource: { role: "filter", active: ["jira_invoices"] },
          d_year: { role: "filter", active: [...apYear] },
        }, "hbar", "desc", EUR_K),
      )
    }
  }
  if (hasSource("costs")) {
    costPanels.push(
      makePanel(t("panel.p.topCostCategories"), ["v_cashflow"], {
        d_account: { role: "xaxis", active: topNBy("d_account", 15, r => r.dataSource === "costs") },
        d_dataSource: { role: "filter", active: ["costs"] },
        d_year: { role: "filter", active: [...latestYear] },
      }, "hbar", "desc", EUR_K),
    )
    if (multiEntity) {
      costPanels.push(
        makePanel(t("panel.p.costByEntity"), ["v_cashflow"], {
          d_entity: { role: "xaxis", active: entities },
          d_dataSource: { role: "filter", active: ["costs"] },
          d_year: { role: "filter", active: [...latestYear] },
        }, "hbar", "desc", EUR_K),
      )
    }
  }
  if (hasSource("jira_invoices") && apYear.length && multiEntity) {
    costPanels.push(
      makePanel(t("panel.p.spendByEntity"), ["v_cashflow"], {
        d_entity: { role: "xaxis", active: entities },
        d_dataSource: { role: "filter", active: ["jira_invoices"] },
        d_year: { role: "filter", active: [...apYear] },
      }, "hbar", "desc", EUR_K),
    )
  }
  if (costPanels.length) {
    presets.push({ id: "cost_control", nameKey: "preset.costControl", Icon: Receipt, panels: costPanels })
  }

  return presets
}

/* ═══ FAQ ═══ */
const FAQ_KEYS = [
  { q: "faq.q1", a: "faq.a1" },
  { q: "faq.q2", a: "faq.a2" },
  { q: "faq.q3", a: "faq.a3" },
  { q: "faq.q4", a: "faq.a4" },
  { q: "faq.q5", a: "faq.a5" },
  { q: "faq.q6", a: "faq.a6" },
  { q: "faq.q7", a: "faq.a7" },
  { q: "faq.q8", a: "faq.a8" },
]

/* ═══ SORTING HELPERS ═══ */
const TIME_FIELDS = new Set(["year", "quarter", "month"])
const isTimeField = (field: string) => TIME_FIELDS.has(field)

/**
 * Cartesian product for multi-dim axis labelling.
 *
 * Used by computeData when a panel has more than one xaxis or split dim.
 * Example: cartesian([["2025","2026"],["Jan","Feb"]]) →
 *   [["2025","Jan"], ["2025","Feb"], ["2026","Jan"], ["2026","Feb"]]
 * Input order is preserved, which matters because time dims come through
 * sorted by FIELD_ORDER and the caller relies on that ordering for the
 * final x-axis tick sequence.
 */
const cartesian = (arrays: string[][]): string[][] => {
  if (arrays.length === 0) return [[]]
  return arrays.reduce<string[][]>((acc, curr) =>
    acc.flatMap(prefix => curr.map(v => [...prefix, v])), [[]])
}
/** Join parts with a visual separator for composite x-axis / split labels. */
const COMPOSITE_SEP = " · "
const joinComposite = (parts: string[]): string => parts.join(COMPOSITE_SEP)

const sortData = (data: Record<string, any>[], series: string[], xKey: string, mode: SortMode): Record<string, any>[] => {
  if (mode === "none") return data
  const sorted = [...data]
  if (mode === "alpha_asc") return sorted.sort((a, b) => String(a[xKey]).localeCompare(String(b[xKey])))
  if (mode === "alpha_desc") return sorted.sort((a, b) => String(b[xKey]).localeCompare(String(a[xKey])))
  // Sort by total numeric value across all series
  const total = (row: Record<string, any>) => series.reduce((s, k) => s + (Number(row[k]) || 0), 0)
  if (mode === "asc") return sorted.sort((a, b) => total(a) - total(b))
  if (mode === "desc") return sorted.sort((a, b) => total(b) - total(a))
  return sorted
}

/* ═══ COMPUTE DATA ═══ */
// Parse value IDs: "v_cashflow" → "Cash Flow", "v_assets" → "Assets Held"
const VALUE_ID_MAP: Record<string, string> = {
  v_cashflow: 'Cash Flow',
  v_assets: 'Assets Held',
  // WWDA flow types
  v_transactionvolume: 'Transaction Volume',
  v_balance: 'Balance',
  v_feerevenue: 'Fee Revenue',
}

const computeData = (panel: PanelConfig, perms?: UserPerms, fxRate = 1.0, tFunc?: (k: string) => string, curLang: Lang = "en"): ChartData => {
  const tr = (v: string) => tFunc ? translateDataValue(tFunc, v, curLang) : v
  const flowTypes = panel.selectedValues.map(id => VALUE_ID_MAP[id]).filter(Boolean)
  if (!flowTypes.length) return { data: [], series: [], xKey: "label" }

  const sp = panel.activeSplits
  // Multi-dim xaxis and split are supported: when the user drops more than
  // one dim pill into the X or S zone we honour all of them in insertion
  // order. Composite x-axis labels are rendered as "v1 · v2 · ..." and
  // composite series keys the same way. Single-dim case collapses to the
  // old behaviour exactly (cartesian of one array = the array itself).
  const xEntries = Object.entries(sp).filter(([, c]) => c.role === "xaxis")
  const splitEntries = Object.entries(sp).filter(([, c]) => c.role === "split")
  const filterEntries = Object.entries(sp).filter(([, c]) => c.role === "filter")
  const xFields = xEntries.map(([id]) => DIM_FIELD[id]).filter(Boolean)
  const splitFields = splitEntries.map(([id]) => DIM_FIELD[id]).filter(Boolean)
  const sort = panel.sortMode || "none"

  // Filter rows matching ANY of the selected flowTypes
  let f = DATA.filter(d => flowTypes.includes(d.flowType))
  if (perms) f = f.filter(d => perms.entities.includes(d.entity) && perms.years.includes(d.year))
  filterEntries.forEach(([id, cfg]) => {
    const fl = DIM_FIELD[id]
    if (fl && cfg.active.length) f = f.filter(d => cfg.active.includes((d as any)[fl]))
  })
  // Apply xaxis and split "active" filters per-dim — each additional
  // xaxis/split dim further narrows the row set the same way filters do.
  xEntries.forEach(([id, cfg]) => {
    const fl = DIM_FIELD[id]
    if (fl && cfg.active.length) {
      const active = new Set(cfg.active)
      f = f.filter(d => active.has((d as any)[fl]))
    }
  })
  splitEntries.forEach(([id, cfg]) => {
    const fl = DIM_FIELD[id]
    if (fl && cfg.active.length) {
      const active = new Set(cfg.active)
      f = f.filter(d => active.has((d as any)[fl]))
    }
  })

  // Build series labels — translate flowType names
  const valueLabels = flowTypes.length === 1 ? [tr(flowTypes[0])] : flowTypes.map(tr)
  const singleValue = flowTypes.length === 1

  // No xaxis dims → single "Total" row with one cell per selected value
  if (xFields.length === 0) {
    const row: Record<string, any> = { label: tFunc ? tFunc("chart.total") : "Total" }
    flowTypes.forEach((ft, i) => {
      row[valueLabels[i]] = f.filter(d => d.flowType === ft).reduce((s, d) => s + d.value * fxRate, 0)
    })
    return { data: [row], series: valueLabels, xKey: "label" }
  }

  // Per-field translation policy: client and counterparty names are real
  // labels and must never be run through the i18n translator.
  const SKIP_TRANSLATE_FIELDS = new Set(["clientName", "counterparty"])
  const trForField = (field: string) =>
    SKIP_TRANSLATE_FIELDS.has(field) ? (v: string) => v : tr
  const trXPerField = xFields.map(trForField)
  const trSPerField = splitFields.map(trForField)

  // Build per-dim ordered value lists. Time fields use FIELD_ORDER;
  // non-time fields merge FIELD_ORDER with any extra values present in
  // the filtered row set (so new categories still render).
  const xOrders: string[][] = xEntries.map(([id, cfg], i) => {
    const field = xFields[i]
    const order = FIELD_ORDER[field] || []
    const active = new Set(cfg.active)
    if (isTimeField(field)) return order.filter(v => active.has(v))
    const dataVals = [...new Set(f.map(d => String((d as any)[field] ?? "")))]
    return [...new Set([
      ...order.filter(v => active.has(v)),
      ...dataVals.filter(v => active.has(v)),
    ])]
  })

  // Cartesian product → ordered list of composite x-axis keys (untranslated).
  // Used to iterate the result map in a stable order. Display labels are
  // embedded per-row via trXKey(rawKey) below.
  const orderedXRawKeys = cartesian(xOrders).map(parts => joinComposite(parts))

  // Row-level key builders: same COMPOSITE_SEP join so rawX/rawSplit keys
  // line up with the cartesian lookup keys above.
  const rawXKey = (d: DataRow): string =>
    joinComposite(xFields.map(field => String((d as any)[field] ?? "")))
  const rawSplitKey = (d: DataRow): string =>
    joinComposite(splitFields.map(field => String((d as any)[field] ?? "")))
  const trXKey = (raw: string): string => {
    const parts = raw.split(COMPOSITE_SEP)
    return joinComposite(parts.map((p, i) => trXPerField[i](p)))
  }
  const trSplitKey = (raw: string): string => {
    const parts = raw.split(COMPOSITE_SEP)
    return joinComposite(parts.map((p, i) => trSPerField[i](p)))
  }

  const xKeyOut = "_x_"  // internal x-axis key to avoid collisions with translated series names

  // Group rows into { rawX → { series → sum } } then emit in orderedX order.
  const res: Record<string, Record<string, any>> = {}

  if (splitFields.length > 0) {
    const seriesSet = new Set<string>()
    f.forEach(d => {
      const rx = rawXKey(d)
      const rs = rawSplitKey(d)
      const sLabel = trSplitKey(rs)
      seriesSet.add(sLabel)
      if (!res[rx]) res[rx] = { [xKeyOut]: trXKey(rx) }
      res[rx][sLabel] = (res[rx][sLabel] || 0) + d.value * fxRate
    })
    const series = [...seriesSet]
    const data = orderedXRawKeys.filter(k => res[k]).map(k => res[k])
    let result: ChartData = { data, series, xKey: xKeyOut }
    if (sort !== "none") result.data = sortData(result.data, result.series, xKeyOut, sort)
    return result
  }

  if (singleValue) {
    const seriesLabel = valueLabels[0]
    f.forEach(d => {
      const rx = rawXKey(d)
      if (!res[rx]) res[rx] = { [xKeyOut]: trXKey(rx), [seriesLabel]: 0 }
      res[rx][seriesLabel] = (res[rx][seriesLabel] || 0) + d.value * fxRate
    })
    let result: ChartData = {
      data: orderedXRawKeys.filter(k => res[k]).map(k => res[k]),
      series: [seriesLabel],
      xKey: xKeyOut,
    }
    if (sort !== "none") result.data = sortData(result.data, result.series, xKeyOut, sort)
    return result
  }

  // Multi value, no split → one series per selected flow type
  f.forEach(d => {
    const rx = rawXKey(d)
    if (!res[rx]) res[rx] = { [xKeyOut]: trXKey(rx) }
    const ftT = tr(d.flowType)
    if (valueLabels.includes(ftT)) {
      res[rx][ftT] = (res[rx][ftT] || 0) + d.value * fxRate
    }
  })
  let result: ChartData = {
    data: orderedXRawKeys.filter(k => res[k]).map(k => res[k]),
    series: valueLabels,
    xKey: xKeyOut,
  }
  if (sort !== "none") result.data = sortData(result.data, result.series, xKeyOut, sort)
  return result
}

/* ═══ EXPORT FUNCTIONS ═══ */
const exportCSV = (data: any[], series: string[], xKey: string, filename: string) => {
  const h = [xKey, ...series]
  const csv = [h.join(","), ...data.map(r => h.map(k => r[k] ?? "").join(","))].join("\n")
  const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `${filename}.csv`; a.click()
}

const exportAllCSV = (panels: PanelConfig[], cd: ChartData[]) => {
  let csv = ""
  panels.forEach((p, i) => {
    if (i) csv += "\n\n"; csv += `--- ${p.title} ---\n`
    const h = [cd[i].xKey, ...cd[i].series]; csv += h.join(",") + "\n"
    cd[i].data.forEach(r => { csv += h.map(k => r[k] ?? "").join(",") + "\n" })
  })
  const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "finance_report_all.csv"; a.click()
}

const exportPDFWithCharts = async (panels: PanelConfig[], panelIds: string[], t?: (k: string) => string) => {
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" })
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  const margin = 15

  // Title page
  pdf.setFontSize(22)
  pdf.text(t ? t("pdf.title") : "Financial Analytics Report", margin, 30)
  pdf.setFontSize(11)
  pdf.setTextColor(120)
  const now = new Date()
  const dateStr = now.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
  const timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  pdf.text(`${t ? t("pdf.generated") : "Generated"} ${dateStr} ${t ? t("pdf.at") : "at"} ${timeStr}`, margin, 40)
  pdf.setTextColor(0)

  for (let i = 0; i < panels.length; i++) {
    const el = document.getElementById(panelIds[i])
    if (!el) continue

    pdf.addPage()
    pdf.setFontSize(16)
    pdf.text(`${i + 1}. ${panels[i].title}`, margin, 20)

    try {
      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
        useCORS: true,
      })
      const imgData = canvas.toDataURL("image/png")
      const imgW = pageW - margin * 2
      const imgH = (canvas.height / canvas.width) * imgW
      const maxH = pageH - 35
      const finalH = Math.min(imgH, maxH)
      const finalW = (finalH / imgH) * imgW
      pdf.addImage(imgData, "PNG", margin, 28, finalW, finalH)
    } catch (err) {
      pdf.setFontSize(11)
      pdf.setTextColor(150)
      pdf.text(t ? t("pdf.captureError") : "Could not capture chart image", margin, 35)
      pdf.setTextColor(0)
    }
  }

  pdf.save("finance_report.pdf")
}

/** Mini sparkline for KPI cards — renders an inline SVG */
const Sparkline = ({ data, positive, width = 80, height = 28 }: { data: number[]; positive?: boolean; width?: number; height?: number }) => {
  if (data.length < 2) return null
  const min = Math.min(...data), max = Math.max(...data)
  const range = max - min || 1
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * (height - 4) - 2
    return `${x},${y}`
  }).join(" ")
  const color = positive !== false ? "var(--color-emerald, #10b981)" : "var(--color-red, #ef4444)"
  const lastPoint = data[data.length - 1]
  const lastX = width
  const lastY = height - ((lastPoint - min) / range) * (height - 4) - 2
  // Build area fill path
  const pathD = `M0,${height} L${data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`).join(" L")} L${width},${height} Z`

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`spark-${positive !== false ? "pos" : "neg"}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <path d={pathD} fill={`url(#spark-${positive !== false ? "pos" : "neg"})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r={2} fill={color} />
    </svg>
  )
}

/* ═══════════════════════════════════════════════════════════
   NATURAL LANGUAGE QUERY ENGINE — parse free-text queries
   into PanelConfig objects the dashboard can render.
   ═══════════════════════════════════════════════════════════ */

interface NLQueryResult {
  panel: PanelConfig
  explanation: string
  confidence: number
}

// Keyword→intent maps
const VALUE_KEYWORDS: Record<string, { values: string[]; dataSourceFilter?: string[] }> = {
  "revenue": { values: ["v_cashflow"], dataSourceFilter: ["revenue"] },
  "income": { values: ["v_cashflow"], dataSourceFilter: ["revenue"] },
  "sales": { values: ["v_cashflow"], dataSourceFilter: ["revenue"] },
  "cost": { values: ["v_cashflow"], dataSourceFilter: ["costs"] },
  "costs": { values: ["v_cashflow"], dataSourceFilter: ["costs"] },
  "expense": { values: ["v_cashflow"], dataSourceFilter: ["costs"] },
  "expenses": { values: ["v_cashflow"], dataSourceFilter: ["costs"] },
  "spend": { values: ["v_cashflow"], dataSourceFilter: ["costs"] },
  "spending": { values: ["v_cashflow"], dataSourceFilter: ["costs"] },
  "aum": { values: ["v_assets"] },
  "auc": { values: ["v_assets"] },
  "assets": { values: ["v_assets"] },
  "positions": { values: ["v_assets"] },
  "portfolio": { values: ["v_assets"] },
  "volume": { values: ["v_transactionvolume"] },
  "transactions": { values: ["v_transactionvolume"] },
  "transaction": { values: ["v_transactionvolume"] },
  "flow": { values: ["v_transactionvolume"] },
  "flows": { values: ["v_transactionvolume"] },
  "balance": { values: ["v_balance"] },
  "balances": { values: ["v_balance"] },
  "fee": { values: ["v_feerevenue"] },
  "fees": { values: ["v_feerevenue"] },
  "commission": { values: ["v_feerevenue"] },
  "profit": { values: ["v_cashflow"], dataSourceFilter: ["revenue", "costs"] },
  "margin": { values: ["v_cashflow"], dataSourceFilter: ["revenue", "costs"] },
  "p&l": { values: ["v_cashflow"], dataSourceFilter: ["revenue", "costs"] },
  "pnl": { values: ["v_cashflow"], dataSourceFilter: ["revenue", "costs"] },
}

const DIM_KEYWORDS: Record<string, string> = {
  "client": "d_clientName", "clients": "d_clientName", "customer": "d_clientName", "customers": "d_clientName",
  "counterpart": "d_counterparty", "counterparty": "d_counterparty", "vendor": "d_counterparty", "vendors": "d_counterparty",
  "category": "d_account", "categories": "d_account", "account": "d_account", "type": "d_costType", "types": "d_costType",
  "year": "d_year", "years": "d_year", "yearly": "d_year", "annual": "d_year", "annually": "d_year",
  "month": "d_month", "months": "d_month", "monthly": "d_month",
  "quarter": "d_quarter", "quarters": "d_quarter", "quarterly": "d_quarter",
  "entity": "d_entity", "entities": "d_entity", "company": "d_entity", "companies": "d_entity",
  "currency": "d_currency", "currencies": "d_currency",
  "source": "d_dataSource", "sources": "d_dataSource",
  "product": "d_contraCategory", "products": "d_contraCategory",
}

const CHART_KEYWORDS: Record<string, ChartType> = {
  "bar": "bar", "bars": "bar", "column": "bar", "columns": "bar",
  "stack": "stackedBar", "stacked": "stackedBar",
  "line": "line", "trend": "line", "trends": "line", "over time": "line", "timeline": "line",
  "area": "area",
  "pie": "pie", "donut": "pie", "breakdown": "pie", "composition": "pie", "share": "pie",
  "horizontal": "hbar",
  "table": "table", "grid": "table", "list": "table", "data": "table", "raw": "table",
}

const NL_EXAMPLE_KEYS = [
  "nl.ex1", "nl.ex2", "nl.ex3", "nl.ex4", "nl.ex5",
  "nl.ex6", "nl.ex7", "nl.ex8", "nl.ex9",
]

function parseNLQuery(query: string): NLQueryResult | null {
  const q = query.toLowerCase().trim()
  if (q.length < 3) return null

  const tokens = q.split(/[\s,;.!?]+/).filter(Boolean)
  const explanation: string[] = []
  let confidence = 0.3

  // ── Extract value/metric ──
  let selectedValues: string[] = []
  let dataSourceFilter: string[] = []
  for (const [kw, cfg] of Object.entries(VALUE_KEYWORDS)) {
    if (q.includes(kw)) {
      selectedValues.push(...cfg.values)
      if (cfg.dataSourceFilter) dataSourceFilter.push(...cfg.dataSourceFilter)
      explanation.push(`Metric: ${kw}`)
      confidence += 0.2
      break
    }
  }
  // Default fallback
  if (!selectedValues.length) {
    // Cash flow is the default metric unless the loaded view has no cash-flow
    // rows at all and does have a wallet book, in which case volume is what
    // the question was most likely about.
    if (HAS_WALLET_BOOK && !DATA.some(d => d.flowType === "Cash Flow")) {
      selectedValues = ["v_transactionvolume"]
      explanation.push("Metric: transaction volume (default)")
    } else {
      selectedValues = ["v_cashflow"]
      explanation.push("Metric: cash flow (default)")
    }
  }
  selectedValues = [...new Set(selectedValues)]

  // ── Extract dimensions (xaxis, split) ──
  const foundDims: string[] = []
  // Check multi-word patterns first
  for (const [kw, dimId] of Object.entries(DIM_KEYWORDS)) {
    if (q.includes(kw) && !foundDims.includes(dimId)) {
      foundDims.push(dimId)
    }
  }

  // Detect "by X" pattern → xaxis, "split by Y" / "grouped by Y" → split
  let xaxisDim: string | null = null
  let splitDim: string | null = null
  const byMatch = q.match(/\bby\s+(\w+)/g)
  if (byMatch) {
    byMatch.forEach((m, idx) => {
      const word = m.replace(/^by\s+/, "")
      const dimId = DIM_KEYWORDS[word]
      if (dimId) {
        // "split by" or "grouped by" or second "by" → split
        const isSplit = q.includes(`split by ${word}`) || q.includes(`grouped by ${word}`) || q.includes(`group by ${word}`) || idx > 0
        if (isSplit && !splitDim) {
          splitDim = dimId
          explanation.push(`Split: ${word}`)
        } else if (!xaxisDim) {
          xaxisDim = dimId
          explanation.push(`X-axis: ${word}`)
        }
      }
    })
  }

  // If "monthly" / "yearly" / "quarterly" with "trend" → xaxis = time, no split needed
  if (!xaxisDim && (q.includes("monthly") || q.includes("month"))) { xaxisDim = "d_month"; explanation.push("X-axis: month") }
  if (!xaxisDim && (q.includes("yearly") || q.includes("annual"))) { xaxisDim = "d_year"; explanation.push("X-axis: year") }
  if (!xaxisDim && (q.includes("quarterly"))) { xaxisDim = "d_quarter"; explanation.push("X-axis: quarter") }

  // "trend" / "over time" without explicit dim → default to month, split by year
  if (!xaxisDim && (q.includes("trend") || q.includes("over time") || q.includes("timeline"))) {
    xaxisDim = "d_month"
    if (!splitDim) splitDim = "d_year"
    explanation.push("X-axis: month (trend mode)")
  }

  // Fallback: use first found dimension
  if (!xaxisDim && foundDims.length > 0) {
    xaxisDim = foundDims[0]
    if (foundDims.length > 1 && !splitDim) splitDim = foundDims[1]
  }

  // Final fallback: default axis by entity
  if (!xaxisDim) {
    xaxisDim = "d_year"
    explanation.push("X-axis: year (default)")
  }
  confidence += 0.15

  // ── Extract chart type ──
  let chartType: ChartType = "bar"
  for (const [kw, ct] of Object.entries(CHART_KEYWORDS)) {
    if (q.includes(kw)) { chartType = ct; explanation.push(`Chart: ${kw}`); confidence += 0.1; break }
  }
  // "trend" → line, "compare" → stacked bar or grouped bar
  if (q.includes("trend") && chartType === "bar") chartType = "line"
  if ((q.includes("compare") || q.includes("vs") || q.includes("versus")) && chartType === "bar" && splitDim) chartType = "bar"

  // ── Extract year filters ──
  let yearFilter: string[] = []
  const yearMatches = q.match(/\b(20[0-9]{2})\b/g)
  if (yearMatches) {
    yearFilter = [...new Set(yearMatches)].filter(y => YEARS.includes(y))
    if (yearFilter.length) {
      explanation.push(`Year filter: ${yearFilter.join(", ")}`)
      confidence += 0.1
    }
  }
  // "this year" / "current year" / "latest"
  if (q.includes("this year") || q.includes("current year") || q.includes("latest")) {
    yearFilter = [YEARS[YEARS.length - 1]]
    explanation.push(`Year: ${yearFilter[0]} (latest)`)
  }
  // "last year" / "previous year"
  if (q.includes("last year") || q.includes("previous year")) {
    if (YEARS.length > 1) yearFilter = [YEARS[YEARS.length - 2]]
  }

  // ── Extract sorting ──
  let sortMode: SortMode = "none"
  if (q.includes("top") || q.includes("biggest") || q.includes("largest") || q.includes("highest") || q.includes("most")) {
    sortMode = "desc"
    explanation.push("Sort: descending (top)")
  } else if (q.includes("bottom") || q.includes("smallest") || q.includes("lowest") || q.includes("least")) {
    sortMode = "asc"
    explanation.push("Sort: ascending (bottom)")
  }

  // ── Build PanelConfig ──
  const xDimDef = ALL_DIMS.find(d => d.id === xaxisDim)
  const splitDimDef = splitDim ? ALL_DIMS.find(d => d.id === splitDim) : null

  const splits: Record<string, SplitConfig> = {}
  if (xDimDef) {
    splits[xaxisDim!] = { role: "xaxis", active: [...xDimDef.values] }
  }
  if (splitDimDef && splitDim) {
    splits[splitDim] = { role: "split", active: [...splitDimDef.values] }
  }
  // Add data source filter
  if (dataSourceFilter.length > 0) {
    splits["d_dataSource"] = { role: "filter", active: dataSourceFilter }
  }
  // Add year filter
  if (yearFilter.length > 0 && xaxisDim !== "d_year") {
    splits["d_year"] = { role: "filter", active: yearFilter }
  }

  // Generate title from query
  const title = query.length > 60 ? query.slice(0, 57) + "..." : query
  // Capitalize first letter
  const displayTitle = title.charAt(0).toUpperCase() + title.slice(1)

  confidence = Math.min(confidence, 1.0)

  return {
    panel: { title: displayTitle, chartType, selectedValues, activeSplits: splits, sortMode },
    explanation: explanation.join(" · "),
    confidence,
  }
}

/** Natural Language Query Bar — the command center */
const NLQueryBar = ({ onQuery }: { onQuery: (panel: PanelConfig) => void }) => {
  const { t } = useI18n()
  const [query, setQuery] = useState("")
  const [result, setResult] = useState<NLQueryResult | null>(null)
  const [showExamples, setShowExamples] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const nlExamples = useMemo(() => NL_EXAMPLE_KEYS.map(k => t(k)), [t])

  const handleSubmit = () => {
    if (!query.trim()) return
    const r = parseNLQuery(query)
    if (r) {
      setResult(r)
      onQuery(r.panel)
      setQuery("")
      setShowExamples(false)
      setTimeout(() => setResult(null), 4000)
    }
  }

  const handleExample = (ex: string) => {
    setQuery(ex)
    setShowExamples(false)
    setTimeout(() => {
      const r = parseNLQuery(ex)
      if (r) { setResult(r); onQuery(r.panel); setQuery(""); setTimeout(() => setResult(null), 4000) }
    }, 100)
  }

  return (
    <div className="shrink-0 relative">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-card shadow-sm">
        <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
        <input ref={inputRef} value={query}
          onChange={e => { setQuery(e.target.value); setResult(null) }}
          onKeyDown={e => { if (e.key === "Enter") handleSubmit(); if (e.key === "Escape") { setQuery(""); setShowExamples(false) } }}
          onFocus={() => !query && setShowExamples(true)}
          onBlur={() => setTimeout(() => setShowExamples(false), 200)}
          placeholder={t("nl.placeholder")}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60 min-w-0" />
        {query && (
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => { setQuery(""); setResult(null) }}>
            <X className="h-3 w-3" />
          </Button>
        )}
        <Button variant="default" size="sm" className="h-6 px-2.5 text-[11px] font-medium shrink-0" onClick={handleSubmit} disabled={!query.trim()}>
          <ArrowRight className="h-3 w-3 mr-1" />{t("nl.query")}
        </Button>
      </div>

      {/* Success feedback */}
      {result && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 px-3 py-2 rounded-lg border bg-emerald-50 dark:bg-emerald-950/50 border-emerald-200 dark:border-emerald-800 text-xs animate-in">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            <span className="font-medium text-emerald-700 dark:text-emerald-300">{t("nl.chartCreated")}</span>
            <span className="text-emerald-600/70 dark:text-emerald-400/70 ml-1">{result.explanation}</span>
          </div>
        </div>
      )}

      {/* Example prompts dropdown */}
      {showExamples && !query && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 rounded-lg border bg-popover shadow-lg p-2 animate-in">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-2 py-1 font-medium">{t("nl.tryAsking")}</p>
          <div className="grid grid-cols-1 gap-0.5">
            {nlExamples.slice(0, 6).map((ex, i) => (
              <button key={i} onMouseDown={e => { e.preventDefault(); handleExample(ex) }}
                className="text-left px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground">
                <span className="text-primary/60 mr-1.5">&rarr;</span>{ex}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ═══ DATA TABLE VIEW (sortable columns) ═══ */
const DataTableView = ({ data, series, xKey }: ChartData) => {
  const { t } = useI18n()
  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  if (!data.length) return <div className="flex items-center justify-center h-full text-muted-foreground text-sm">{t("chart.noData")}</div>

  const handleSort = (col: string) => {
    if (sortCol === col) { setSortDir(d => d === "asc" ? "desc" : "asc") }
    else { setSortCol(col); setSortDir(col === xKey ? "asc" : "desc") }
  }

  const sorted = sortCol ? [...data].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol]
    const numA = Number(av), numB = Number(bv)
    const cmp = (!isNaN(numA) && !isNaN(numB)) ? numA - numB : String(av ?? "").localeCompare(String(bv ?? ""))
    return sortDir === "asc" ? cmp : -cmp
  }) : data

  const arrow = (col: string) => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : ""

  return (
    <div className="h-full w-full overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left sticky top-0 bg-background z-10 cursor-pointer select-none hover:text-foreground" onClick={() => handleSort(xKey)}>{xKey}{arrow(xKey)}</TableHead>
            {series.map(s => <TableHead key={s} className="text-right sticky top-0 bg-background z-10 cursor-pointer select-none hover:text-foreground" onClick={() => handleSort(s)}>{s}{arrow(s)}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((row, i) => (
            <TableRow key={i}>
              <TableCell className="font-medium">{row[xKey]}</TableCell>
              {series.map(s => <TableCell key={s} className="text-right tabular-nums">{(row[s] ?? 0).toLocaleString()}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/* ═══ CHART RENDERER ═══ */
/** Build a ChartConfig from data series */
const buildChartConfig = (series: string[]): ChartConfig => {
  const config: ChartConfig = {}
  series.forEach((k, i) => {
    config[k] = { label: k, color: PALETTE[i % PALETTE.length] }
  })
  return config
}


/**
 * Density warning.
 *
 * Two dimensions on the same shelf are combined with a cartesian product —
 * 12 months × 20 clients is 240 x-axis categories, and two split dims produce
 * the same blow-up in series. Nothing stopped that, and past PALETTE.length
 * series the colours simply wrap, so two unrelated clients render in the
 * identical colour and the chart stays legible-looking while being unreadable.
 * The counts are not capped — silently dropping data would be worse than
 * showing too much — the user is just told what happened.
 */
const CATEGORY_WARN_AT = 60

/** The fin-model forecast sources. Everything else is booked actuals. */
const PLAN_SOURCES = new Set(["budget_expense", "budget_payroll", "pl_plan"])

/**
 * Which data sources a panel actually draws on.
 *
 * A dataSource constraint in any role narrows the set; with no constraint at
 * all the measure sweeps in every source carrying that flow type, which for
 * "Cash Flow" is nine of them.
 */
function effectiveSources(panel: PanelConfig): Set<string> {
  const cfg = panel.activeSplits["d_dataSource"]
  if (cfg && cfg.active.length) return new Set(cfg.active)
  const flows = new Set(panel.selectedValues.map(v => VALUE_ID_MAP[v]).filter(Boolean))
  const out = new Set<string>()
  for (const r of DATA) if (!flows.size || flows.has(r.flowType)) out.add(r.dataSource)
  return out
}

/**
 * Panels that add together numbers which must not be added.
 *
 * flowType "Cash Flow" spans booked actuals, the fin-model plan, the
 * synthesized net line and the per-employee payroll breakdown, so a chart
 * that does not constrain dataSource sums all of them into one bar. Three
 * specific collisions are worth naming:
 *
 *  - plan + actual, which reads as one figure but is forecast plus history;
 *  - `costs` + `payroll_actual`, which are the same payroll twice — 1C books
 *    it to account 800300 inside `costs`, and payroll_actual restates it per
 *    employee (month by month the two agree to within a rounding error);
 *  - `net` + either leg it was computed from, since net is revenue - costs.
 *
 * None of this applies when dataSource is on the x-axis or split shelf: the
 * sources are then drawn as separate series and nothing is being added.
 */
function panelIntegrityWarnings(panel: PanelConfig): string[] {
  const dsCfg = panel.activeSplits["d_dataSource"]
  if (dsCfg && (dsCfg.role === "split" || dsCfg.role === "xaxis")) return []
  const srcs = effectiveSources(panel)
  const scenCfg = panel.activeSplits["d_scenario"]
  const scen = scenCfg && scenCfg.active.length ? new Set(scenCfg.active) : null
  const scenSeparated = scenCfg && (scenCfg.role === "split" || scenCfg.role === "xaxis")

  const out: string[] = []
  const plan = [...srcs].some(s => PLAN_SOURCES.has(s)) && (!scen || scen.has("Plan"))
  const actual = [...srcs].some(s => !PLAN_SOURCES.has(s)) && (!scen || scen.has("Actual"))
  if (plan && actual && !scenSeparated) out.push("warn.planActual")
  if (srcs.has("costs") && srcs.has("payroll_actual")) out.push("warn.payrollDouble")
  if (srcs.has("net") && (srcs.has("costs") || srcs.has("revenue"))) out.push("warn.netDouble")
  return out
}

const ChartWarnings = ({ data, series, panel }: { data: Record<string, any>[]; series: string[]; panel?: PanelConfig }) => {
  const { t } = useI18n()
  const msgs: string[] = []
  if (panel) msgs.push(...panelIntegrityWarnings(panel).map(k => t(k)))
  if (series.length > PALETTE.length) {
    msgs.push(t("warn.manySeries").replace("{n}", String(series.length)).replace("{max}", String(PALETTE.length)))
  } else if (data.length > CATEGORY_WARN_AT) {
    msgs.push(t("warn.manyCategories").replace("{n}", String(data.length)))
  }
  if (!msgs.length) return null
  return (
    <div className="shrink-0 flex flex-col gap-0.5 px-2 py-1 mb-1 rounded border border-amber-300/70 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300">
      {msgs.map((m, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span className="text-[10px] leading-tight">{m}</span>
        </div>
      ))}
    </div>
  )
}

const ChartRenderer = (props: ChartData & { type: ChartType; id: string; yAxis?: YAxisConfig; onYAxisClick?: () => void; panel?: PanelConfig }) => (
  <div className="h-full flex flex-col min-h-0">
    <ChartWarnings data={props.data} series={props.series} panel={props.panel} />
    <div className="flex-1 min-h-0"><ChartBody {...props} /></div>
  </div>
)

const ChartBody = ({ type, data, series, xKey, id, yAxis, onYAxisClick }: ChartData & { type: ChartType; id: string; yAxis?: YAxisConfig; onYAxisClick?: () => void }) => {
  const { t } = useI18n()
  if (type === "table") return <DataTableView data={data} series={series} xKey={xKey} />
  if (!data.length) return <div className="flex items-center justify-center h-full text-muted-foreground text-sm">{t("chart.noDataConfig")}</div>

  const ya = yAxis || {}
  const yMag = ya.magnitude || "auto"
  const yDec = ya.decimals ?? 0
  const yFmt = (v: number) => fmtY(v, yMag, yDec)
  const yDomain: [any, any] = [
    ya.min !== undefined && ya.min !== "auto" ? ya.min : "auto",
    ya.max !== undefined && ya.max !== "auto" ? ya.max : "auto",
  ]
  const yTicks = ya.tickInterval && ya.tickInterval !== "auto"
    ? (() => {
        const lo = typeof yDomain[0] === "number" ? yDomain[0] : Math.min(0, ...data.flatMap(r => series.map(s => Number(r[s]) || 0)))
        const hi = typeof yDomain[1] === "number" ? yDomain[1] : Math.max(0, ...data.flatMap(r => series.map(s => Number(r[s]) || 0)))
        const step = ya.tickInterval as number
        const ticks: number[] = []
        for (let v = Math.floor(lo / step) * step; v <= hi + step * 0.01; v += step) ticks.push(v)
        return ticks
      })()
    : undefined

  const chartConfig = buildChartConfig(series)
  const stk = type === "stackedBar" || type === "hbarStacked"
  const isHoriz = type === "hbar" || type === "hbarStacked"

  // Smart x-axis: auto-skip ticks when too many, truncate long labels, angle when needed
  const maxVisibleTicks = 12
  const manyTicks = data.length > 6
  const tooManyTicks = data.length > maxVisibleTicks
  const xInterval = tooManyTicks ? Math.ceil(data.length / maxVisibleTicks) - 1 : 0
  const truncLabel = (val: any) => { const s = String(val); return s.length > 14 ? s.slice(0, 13) + "…" : s }
  const xTickAngle = manyTicks ? -35 : 0
  const xTickAnchor = manyTicks ? "end" : "middle"

  // Gradient ID generator
  const gid = (k: string) => `grad-${id}-${k.replace(/\s+/g, '_')}`

  // Shared grid style (CRM-matching: dashed, subtle)
  const gridProps = { strokeDasharray: "3 3", stroke: "hsl(var(--border))", strokeWidth: 1 }

  // Shared tooltip style (CRM-matching)
  const tooltipStyle = {
    contentStyle: {
      backgroundColor: "hsl(var(--card))",
      border: "1px solid hsl(var(--border))",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
      fontSize: "12px",
    },
    cursor: { fill: "rgba(0, 0, 0, 0.04)" } as any,
  }

  const yAxisEl = (
    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false}
      tickFormatter={yFmt} width={58} domain={yDomain} ticks={yTicks} allowDataOverflow={yDomain[0] !== "auto" || yDomain[1] !== "auto"}
      tickMargin={4} style={{ fontSize: '11px' }}
    />
  )

  // Legend overlaid inside chart area at top-right — height=0 so it doesn't consume chart space
  const legendEl = series.length > 1 ? (
    <Legend verticalAlign="top" align="right" height={0}
      wrapperStyle={{ fontSize: '11px', lineHeight: '16px', top: 0, right: 0, pointerEvents: 'auto' }}
      iconType="square" iconSize={8}
      formatter={(value: string) => <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '10px', marginRight: '6px' }}>{value}</span>}
    />
  ) : null

  // ── Pie ──
  if (type === "pie") {
    const pieData = series.length === 1
      ? data.map(d => ({ name: d[xKey], value: Number(d[series[0]]) || 0 }))
      : data.map(d => ({ name: d[xKey], value: series.reduce((s, k) => s + (Number(d[k]) || 0), 0) }))

    const pieConfig: ChartConfig = {}
    pieData.forEach((d, i) => { pieConfig[d.name] = { label: d.name, color: PALETTE[i % PALETTE.length] } })

    return (
      <div id={id} className="w-full h-full">
        <ChartContainer config={pieConfig} className="min-h-[200px] w-full h-full">
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="72%" innerRadius="38%" paddingAngle={2}
              stroke="none">
              {pieData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend verticalAlign="bottom" content={<ChartLegendContent />} />
          </PieChart>
        </ChartContainer>
      </div>
    )
  }

  // ── Horizontal Bar ──
  if (isHoriz) {
    const mg = { top: 8, right: 24, bottom: 4, left: 4 }
    return (
      <div id={id} className="w-full h-full">
        <ChartContainer config={chartConfig} className="min-h-[200px] w-full h-full">
          <BarChart data={data} layout="vertical" margin={mg}>
            <CartesianGrid horizontal={false} {...gridProps} />
            <YAxis dataKey={xKey} type="category" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false} width={90} tickFormatter={truncLabel} tickMargin={6} />
            <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false} tickFormatter={yFmt} tickMargin={8} />
            <ChartTooltip {...tooltipStyle} content={<ChartTooltipContent indicator={stk ? "line" : "dot"} />} />
            {legendEl}
            {series.map((k, i) => (
              <Bar key={k} dataKey={k} fill={PALETTE[i % PALETTE.length]}
                radius={stk ? 0 : [0, 4, 4, 0] as any} stackId={stk ? "s" : undefined} name={k} />
            ))}
          </BarChart>
        </ChartContainer>
      </div>
    )
  }

  // ── Vertical Bar / Stacked Bar ──
  if (type === "bar" || type === "stackedBar") {
    const bottomMargin = manyTicks ? 50 : 8
    const mg = { top: 8, right: 16, bottom: bottomMargin, left: 4 }

    return (
      <div id={id} className="w-full h-full">
        <ChartContainer config={chartConfig} className="min-h-[200px] w-full h-full">
          <BarChart data={data} margin={mg}>
            <defs>
              {series.map((k, i) => {
                const color = PALETTE[i % PALETTE.length]
                return (
                  <linearGradient key={k} id={gid(k)} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.9} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.7} />
                  </linearGradient>
                )
              })}
            </defs>
            <CartesianGrid vertical={false} {...gridProps} />
            <XAxis dataKey={xKey}
              tick={{ fontSize: 11, angle: xTickAngle, textAnchor: xTickAnchor, ...(manyTicks ? { dy: 6 } : {}) }}
              stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false}
              interval={xInterval} tickMargin={manyTicks ? 4 : 10}
              tickFormatter={truncLabel} style={{ fontSize: '11px' }} />
            {yAxisEl}
            <ChartTooltip {...tooltipStyle} content={<ChartTooltipContent indicator={stk ? "line" : "dot"} />} />
            {legendEl}
            {series.map((k, i) => (
              <Bar key={k} dataKey={k} fill={`url(#${gid(k)})`}
                radius={stk ? (i === series.length - 1 ? [4, 4, 0, 0] as any : 0) : [4, 4, 0, 0] as any}
                stackId={stk ? "s" : undefined} name={k} />
            ))}
          </BarChart>
        </ChartContainer>
      </div>
    )
  }

  // ── Line / Area ──
  const C = type === "line" ? LineChart : AreaChart
  const bottomMargin = manyTicks ? 50 : 8
  const mg = { top: 8, right: 16, bottom: bottomMargin, left: 4 }

  return (
    <div id={id} className="w-full h-full">
      <ChartContainer config={chartConfig} className="min-h-[200px] w-full h-full">
        <C data={data} margin={mg}>
          <defs>
            {series.map((k, i) => {
              const color = PALETTE[i % PALETTE.length]
              return (
                <linearGradient key={k} id={gid(k)} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              )
            })}
          </defs>
          <CartesianGrid vertical={false} {...gridProps} />
          <XAxis dataKey={xKey}
            tick={{ fontSize: 11, angle: xTickAngle, textAnchor: xTickAnchor, ...(manyTicks ? { dy: 6 } : {}) }}
            stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false}
            interval={xInterval} tickMargin={manyTicks ? 4 : 10}
            tickFormatter={truncLabel} style={{ fontSize: '11px' }} />
          {yAxisEl}
          <ChartTooltip cursor={false} {...tooltipStyle} content={<ChartTooltipContent indicator="dot" />} />
          {legendEl}
          {series.map((k, i) => type === "line" ? (
            <Line key={k} dataKey={k} stroke={PALETTE[i % PALETTE.length]} type="monotone"
              strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: "hsl(var(--background))", fill: PALETTE[i % PALETTE.length] }} name={k} />
          ) : (
            <Area key={k} dataKey={k} stroke={PALETTE[i % PALETTE.length]}
              fill={`url(#${gid(k)})`} fillOpacity={0.4}
              type="monotone" strokeWidth={2} stackId="a" name={k} />
          ))}
        </C>
      </ChartContainer>
    </div>
  )
}

/* ═══ VALUE TREE ═══ */
const ValueFolder = ({ group, selected, onToggle, search }: {
  group: { id: string; label: string; children: { id: string; label: string }[] }
  selected: string[]; onToggle: (id: string) => void; search: string
}) => {
  const { t } = useI18n()
  const [open, setOpen] = useState(true)
  const localizedLabel = (id: string, fallback: string) => valLabel(t, id) || fallback
  const filtered = group.children.filter(c => {
    const lbl = localizedLabel(c.id, c.label)
    return !search || lbl.toLowerCase().includes(search.toLowerCase())
  })
  if (!filtered.length) return null
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md hover:bg-accent text-sm font-medium text-muted-foreground cursor-pointer">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {open ? <FolderOpen className="h-3.5 w-3.5 text-amber-600" /> : <Folder className="h-3.5 w-3.5 text-amber-600" />}
        <span className="flex-1 text-left">{localizedLabel(group.id, group.label)}</span>
        <span className="text-[10px]">{group.children.filter(c => selected.includes(c.id)).length}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {filtered.map(item => {
          const sel = selected.includes(item.id)
          return (
            <div key={item.id} draggable
              role="button"
              tabIndex={0}
              aria-pressed={sel}
              onDragStart={e => setDragPayload(e, DND_VALUE, item.id)}
              onClick={() => onToggle(item.id)}
              onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(item.id) } }}
              className={`flex items-center gap-2 w-full pl-9 pr-2 py-1 rounded-md text-sm cursor-grab active:cursor-grabbing transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${sel ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300" : "hover:bg-accent"}`}>
              <Checkbox checked={sel} className="pointer-events-none" />
              {localizedLabel(item.id, item.label)}
            </div>
          )
        })}
      </CollapsibleContent>
    </Collapsible>
  )
}

/* ═══ Y-AXIS EDITOR — floating draggable panel ═══ */
const YAxisEditor = ({ config, onChange, onClose }: { config: YAxisConfig; onChange: (c: YAxisConfig) => void; onClose: () => void }) => {
  const { t } = useI18n()
  const [pos, setPos] = useState({ x: -999, y: -999 })
  const [initialized, setInitialized] = useState(false)
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)

  useEffect(() => {
    if (!initialized) {
      setPos({ x: Math.max(80, window.innerWidth / 2 - 120), y: Math.max(100, window.innerHeight / 2 - 140) })
      setInitialized(true)
    }
  }, [initialized])

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault()
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y }
    const onMove = (ev: PointerEvent) => {
      if (!dragRef.current) return
      setPos({ x: dragRef.current.origX + ev.clientX - dragRef.current.startX, y: dragRef.current.origY + ev.clientY - dragRef.current.startY })
    }
    const onUp = () => { dragRef.current = null; window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp) }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
  }

  const parseNum = (s: string): number | "auto" => { const n = parseFloat(s); return isNaN(n) ? "auto" : n }

  return (
    <div className="fixed w-[240px] rounded-lg border shadow-2xl z-[100] overflow-hidden flex flex-col"
      style={{ left: pos.x, top: pos.y, backdropFilter: "blur(20px)", background: "rgba(255,255,255,0.94)" }}>
      {/* Drag handle */}
      <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-border shrink-0 cursor-grab active:cursor-grabbing select-none"
        onPointerDown={onPointerDown}>
        <div className="flex flex-col gap-[2px] mr-1 opacity-40">
          <div className="flex gap-[2px]"><span className="w-1 h-1 rounded-full bg-current" /><span className="w-1 h-1 rounded-full bg-current" /></div>
          <div className="flex gap-[2px]"><span className="w-1 h-1 rounded-full bg-current" /><span className="w-1 h-1 rounded-full bg-current" /></div>
          <div className="flex gap-[2px]"><span className="w-1 h-1 rounded-full bg-current" /><span className="w-1 h-1 rounded-full bg-current" /></div>
        </div>
        <span className="text-xs font-semibold flex-1">{t("yaxis.title")}</span>
        <button onClick={onClose} className="p-1 rounded hover:bg-black/10 text-muted-foreground"><X className="h-3 w-3" /></button>
      </div>
      <div className="p-3 space-y-3 text-xs">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] text-muted-foreground">{t("yaxis.min")}</Label>
            <Input className="h-7 text-xs mt-0.5" placeholder={t("yaxis.auto").toLowerCase()}
              value={config.min !== undefined && config.min !== "auto" ? String(config.min) : ""}
              onChange={e => onChange({ ...config, min: parseNum(e.target.value) })} />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">{t("yaxis.max")}</Label>
            <Input className="h-7 text-xs mt-0.5" placeholder={t("yaxis.auto").toLowerCase()}
              value={config.max !== undefined && config.max !== "auto" ? String(config.max) : ""}
              onChange={e => onChange({ ...config, max: parseNum(e.target.value) })} />
          </div>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">{t("yaxis.magnitude")}</Label>
          <Select value={config.magnitude || "auto"} onValueChange={(v: Magnitude) => onChange({ ...config, magnitude: v })}>
            <SelectTrigger className="h-7 text-xs mt-0.5"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[200]">
              <SelectItem value="auto">{t("yaxis.auto")}</SelectItem>
              <SelectItem value="ones">{t("yaxis.ones")}</SelectItem>
              <SelectItem value="thousands">{t("yaxis.thousands")}</SelectItem>
              <SelectItem value="millions">{t("yaxis.millions")}</SelectItem>
              <SelectItem value="billions">{t("yaxis.billions")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">{t("yaxis.decimals")}</Label>
          <Select value={String(config.decimals ?? 0)} onValueChange={v => onChange({ ...config, decimals: parseInt(v) })}>
            <SelectTrigger className="h-7 text-xs mt-0.5"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[200]">
              {[0, 1, 2, 3].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" className="w-full h-7 text-[10px]"
          onClick={() => onChange({})}>
          {t("yaxis.reset")}
        </Button>
      </div>
    </div>
  )
}

/* ═══ DRAGGABLE FLOATING EDITOR — filter values for a dimension ═══ */
const FloatingEditor = ({ dimId, config, onUpdate, onRemove, onClose }: {
  dimId: string; config: SplitConfig
  onUpdate: (id: string, cfg: SplitConfig) => void
  onRemove: (id: string) => void
  onClose: () => void
}) => {
  const { t, lang } = useI18n()
  const [search, setSearch] = useState("")
  const [pos, setPos] = useState({ x: -999, y: -999 })
  const [initialized, setInitialized] = useState(false)
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const dim = ALL_DIMS.find(d => d.id === dimId)

  // Position near center of viewport on first open
  useEffect(() => {
    if (!initialized) {
      setPos({ x: Math.max(60, window.innerWidth / 2 - 110), y: Math.max(80, window.innerHeight / 2 - 160) })
      setInitialized(true)
    }
  }, [initialized])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null
      if (!el || (panelRef.current && panelRef.current.contains(el))) return
      // The role Select renders in a portal, so its options sit outside
      // panelRef. Treating them as "outside" would close the editor the
      // instant you picked a role.
      if (el.closest?.('[data-radix-popper-content-wrapper],[role="listbox"],[role="option"]')) return
      // A click on this dim's own pill means "toggle me shut" and is handled
      // by the pill's onClick. Closing here as well would clear openDim on
      // mousedown, so the click that follows would read it as already-closed
      // and reopen the editor — making the pill impossible to click shut.
      if (el.closest?.(`[data-dim-pill="${dimId}"]`)) return
      onClose()
    }
    // Delay to avoid closing immediately on the same click that opened it
    const timer = setTimeout(() => document.addEventListener("mousedown", handler), 0)
    return () => { clearTimeout(timer); document.removeEventListener("mousedown", handler) }
  }, [onClose, dimId])

  if (!dim) return null
  const vals = dim.values
  const active = config.active
  const filtered = vals.filter(v => !search || v.toLowerCase().includes(search.toLowerCase()))
  const allOn = active.length === vals.length

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault()
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y }
    const onMove = (ev: PointerEvent) => {
      if (!dragRef.current) return
      setPos({ x: dragRef.current.origX + ev.clientX - dragRef.current.startX, y: dragRef.current.origY + ev.clientY - dragRef.current.startY })
    }
    const onUp = () => { dragRef.current = null; window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp) }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
  }

  // bg-popover, not a hardcoded white: the inner text uses theme tokens, so a
  // literal rgba(255,255,255,…) background rendered near-white text on a white
  // panel for every dark-theme user.
  return (
    <div ref={panelRef} className="fixed w-[240px] max-h-[380px] rounded-lg border shadow-2xl z-[100] overflow-hidden flex flex-col bg-popover/95 text-popover-foreground"
      style={{ left: pos.x, top: pos.y, backdropFilter: "blur(20px)" }}>
      {/* Drag handle + header */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border shrink-0 cursor-grab active:cursor-grabbing select-none"
        onPointerDown={onPointerDown}>
        <div className="flex flex-col gap-[2px] mr-1 opacity-40">
          <div className="flex gap-[2px]"><span className="w-1 h-1 rounded-full bg-current" /><span className="w-1 h-1 rounded-full bg-current" /></div>
          <div className="flex gap-[2px]"><span className="w-1 h-1 rounded-full bg-current" /><span className="w-1 h-1 rounded-full bg-current" /></div>
          <div className="flex gap-[2px]"><span className="w-1 h-1 rounded-full bg-current" /><span className="w-1 h-1 rounded-full bg-current" /></div>
        </div>
        <span className="text-xs font-semibold flex-1 truncate">{dimLabel(t, dimId, dim.label)}</span>
        <Badge variant="secondary" className="text-[9px] h-4 bg-background/60 pointer-events-none">{active.length}/{vals.length}</Badge>
        <button onClick={e => { e.stopPropagation(); onRemove(dimId) }} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
          <Trash2 className="h-3 w-3" />
        </button>
        <button onClick={e => { e.stopPropagation(); onClose() }} className="p-1 rounded hover:bg-accent text-muted-foreground">
          <X className="h-3 w-3" />
        </button>
      </div>
      {/* Role switcher — until now the ONLY way to move a dimension between
          the X / Filter / Split shelves was to drag its pill, which left no
          path at all for keyboard users. */}
      <div className="flex items-center gap-1.5 px-2.5 pt-2 shrink-0">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">{t("editor.role")}</span>
        <Select value={config.role} onValueChange={(v: DimRole) => onUpdate(dimId, { ...config, role: v })}>
          <SelectTrigger className="h-6 text-[11px] flex-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(["xaxis", "filter", "split"] as DimRole[]).map(r => (
              <SelectItem key={r} value={r}>
                <div className="flex flex-col">
                  <span className="font-medium">{t(`zone.${r}`)}</span>
                  <span className="text-[10px] text-muted-foreground">{t(`role.${r}Desc`)}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Search + bulk actions */}
      <div className="px-2.5 pt-2 pb-1 shrink-0">
        <Input placeholder={t("editor.search")} value={search} onChange={e => setSearch(e.target.value)} className="h-6 text-[11px] bg-background/70 border-border" />
        <div className="flex gap-1 mt-1.5">
          <button onClick={() => onUpdate(dimId, { ...config, active: allOn ? [] : [...vals] })}
            className="text-[10px] px-2 py-0.5 rounded bg-background/60 hover:bg-background border border-border font-medium">
            {allOn ? t("editor.clear") : t("editor.all")}
          </button>
          <button onClick={() => onUpdate(dimId, { ...config, active: vals.filter(v => !active.includes(v)) })}
            className="text-[10px] px-2 py-0.5 rounded bg-background/60 hover:bg-background border border-border font-medium">
            {t("editor.invert")}
          </button>
        </div>
      </div>
      {/* Values list — labels go through the display map so the checkboxes
          read "Payroll (plan)" rather than the raw "budget_payroll" key. */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {filtered.map(v => {
          const on = active.includes(v)
          return (
            <label key={v} className="flex items-center gap-2 py-0.5 px-1.5 rounded text-[11px] cursor-pointer hover:bg-background/60">
              <Checkbox checked={on} onCheckedChange={() =>
                onUpdate(dimId, { ...config, active: on ? active.filter(x => x !== v) : [...active, v] })
              } />
              <span className={on ? "text-foreground font-medium" : "text-muted-foreground"}>{translateDataValue(t, v, lang)}</span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

/* ═══ DROP ZONE — vertical bucket that accepts dragged dimension pills ═══ */

/**
 * Drags carry a marker MIME type alongside the payload so a zone can tell a
 * dimension from a value *during* dragover, when getData() is deliberately
 * blocked by the browser. Without it the only option was to accept every drag
 * and silently ignore the wrong ones — indistinguishable, from the user's
 * side, from a drag that simply didn't work.
 */
const DND_DIM = "application/x-nf-dim"
const DND_VALUE = "application/x-nf-value"

const setDragPayload = (e: React.DragEvent, kind: string, id: string) => {
  e.dataTransfer.setData("text/plain", id)
  e.dataTransfer.setData(kind, id)
  e.dataTransfer.effectAllowed = "move"
}

/** True when the cursor left the element for good, rather than for a child. */
const leftForGood = (e: React.DragEvent) => {
  const rel = e.relatedTarget as Node | null
  return !rel || !(e.currentTarget as HTMLElement).contains(rel)
}

const ZONE_STYLES: Record<DimRole, { i18nKey: string; tag: string; bg: string; border: string; text: string; dropBg: string; pill: string; pillHover: string }> = {
  xaxis:  { i18nKey: "zone.xaxis", tag: "X", bg: "bg-blue-50/50 dark:bg-blue-950/30",   border: "border-blue-200/70 dark:border-blue-800/50", text: "text-blue-500 dark:text-blue-400", dropBg: "bg-blue-100 dark:bg-blue-900/40", pill: "border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300", pillHover: "hover:bg-blue-100 dark:hover:bg-blue-900/50" },
  filter: { i18nKey: "zone.filter", tag: "F", bg: "bg-green-50/50 dark:bg-green-950/30",  border: "border-green-200/70 dark:border-green-800/50", text: "text-green-500 dark:text-green-400", dropBg: "bg-green-100 dark:bg-green-900/40", pill: "border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300", pillHover: "hover:bg-green-100 dark:hover:bg-green-900/50" },
  split:  { i18nKey: "zone.split",  tag: "S", bg: "bg-amber-50/50 dark:bg-amber-950/30",  border: "border-amber-200/70 dark:border-amber-800/50", text: "text-amber-500 dark:text-amber-400", dropBg: "bg-amber-100 dark:bg-amber-900/40", pill: "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300", pillHover: "hover:bg-amber-100 dark:hover:bg-amber-900/50" },
}

const DropZone = ({ role, dims, onDrop, onClickPill, onRemovePill, dragOverRole, setDragOverRole, activeSources = [] }: {
  role: DimRole
  dims: [string, SplitConfig][]
  onDrop: (dimId: string, newRole: DimRole) => void
  onClickPill: (dimId: string) => void
  onRemovePill: (dimId: string) => void
  dragOverRole: DimRole | null
  setDragOverRole: (r: DimRole | null) => void
  /** data sources currently contributing to this panel, for the applicability check */
  activeSources?: string[]
}) => {
  const { t } = useI18n()
  const s = ZONE_STYLES[role]
  const isOver = dragOverRole === role

  return (
    <div
      className={`min-h-[26px] px-1.5 py-1 rounded border border-dashed transition-colors ${s.border} ${isOver ? s.dropBg : s.bg}`}
      onDragOver={e => {
        // Not preventing default is what produces the "no drop" cursor, so a
        // value dragged onto a dimension shelf now reads as refused rather
        // than as a broken drag.
        if (!e.dataTransfer.types.includes(DND_DIM)) { e.dataTransfer.dropEffect = "none"; return }
        e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverRole(role)
      }}
      onDragLeave={e => { if (leftForGood(e)) setDragOverRole(null) }}
      onDrop={e => { e.preventDefault(); setDragOverRole(null); const id = e.dataTransfer.getData("text/plain"); if (id) onDrop(id, role) }}
    >
      <div className={`text-[9px] font-bold uppercase tracking-wider mb-0.5 ${s.text}`}>{s.tag}</div>
      {dims.length === 0 && <div className="text-[9px] text-muted-foreground/50 italic">{t("zone.drop")}</div>}
      <div className="flex flex-col gap-0.5">
        {dims.map(([id, cfg]) => {
          // A dimension that does not apply to one of the panel's sources
          // collapses all of that source's rows into a single placeholder
          // bucket — a chart that looks right and means nothing. Filters are
          // exempt: excluding rows is a legitimate use.
          const bad = role === "filter" ? [] : incompatibleSources(id, activeSources)
          const total = ALL_DIMS.find(d => d.id === id)?.values.length ?? cfg.active.length
          // active-of-total, not a bare count: "12" alone reads the same
          // whether it is 12 of 12 or 12 of 340, so a pill could hide the
          // fact that 96% of the rows are excluded.
          const narrowed = cfg.active.length < total
          const pill = (
            <span key={id} draggable
              data-dim-pill={id}
              role="button"
              tabIndex={0}
              aria-label={`${dimLabel(t, id)} — ${cfg.active.length} of ${total} selected`}
              onDragStart={e => setDragPayload(e, DND_DIM, id)}
              onClick={e => { e.stopPropagation(); onClickPill(id) }}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onClickPill(id) }
                if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); e.stopPropagation(); onRemovePill(id) }
              }}
              className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded border text-[10px] font-medium cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${s.pill} ${s.pillHover} select-none ${bad.length ? "ring-1 ring-amber-500/70" : ""}`}>
              {bad.length > 0 && <AlertTriangle className="h-2.5 w-2.5 shrink-0 text-amber-500" />}
              <span className="truncate max-w-[84px]">{dimLabel(t, id)}</span>
              <span className={`text-[9px] tabular-nums shrink-0 ${narrowed ? "text-amber-600 dark:text-amber-400 font-semibold" : "opacity-40"}`}>
                {cfg.active.length}/{total}
              </span>
              <button onClick={e => { e.stopPropagation(); onRemovePill(id) }} tabIndex={-1} aria-hidden
                className="opacity-30 hover:opacity-100 hover:text-destructive ml-auto shrink-0">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          )
          // Native title= is slow to appear and never shows on touch, which
          // made the applicability warning easy to miss entirely.
          return bad.length ? (
            <UITooltip key={id} delayDuration={200}>
              <TooltipTrigger asChild>{pill}</TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px] text-[11px]">
                {t("zone.incompatible").replace("{dim}", dimLabel(t, id)).replace("{sources}", bad.join(", "))}
              </TooltipContent>
            </UITooltip>
          ) : pill
        })}
      </div>
    </div>
  )
}

/* ═══ VALUE DROP ZONE — accepts any value ID from VALUE_ID_MAP ═══ */
const VALUE_IDS = Object.keys(VALUE_ID_MAP)

const ValueDropZone = ({ selectedValues, onUpdate, dragOverValue, setDragOverValue }: {
  selectedValues: string[]
  onUpdate: (vals: string[]) => void
  dragOverValue: boolean
  setDragOverValue: (v: boolean) => void
}) => {
  const { t } = useI18n()
  const val = selectedValues[0] // max one
  return (
    <div
      className={`min-h-[26px] px-1.5 py-1 rounded border border-dashed transition-colors ${dragOverValue ? "bg-purple-100 dark:bg-purple-900/40 border-purple-300 dark:border-purple-600" : "bg-purple-50/50 dark:bg-purple-950/30 border-purple-200/70 dark:border-purple-800/50"}`}
      onDragOver={e => {
        if (!e.dataTransfer.types.includes(DND_VALUE)) { e.dataTransfer.dropEffect = "none"; return }
        e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverValue(true)
      }}
      onDragLeave={e => { if (leftForGood(e)) setDragOverValue(false) }}
      onDrop={e => {
        e.preventDefault(); setDragOverValue(false)
        const id = e.dataTransfer.getData("text/plain")
        if (id && VALUE_IDS.includes(id)) onUpdate([id])
      }}
    >
      <div className="text-[9px] font-bold uppercase tracking-wider mb-0.5 text-purple-500 dark:text-purple-400">{t("zone.value")}</div>
      {!val && <div className="text-[9px] text-muted-foreground/50 italic">{t("zone.drop")}</div>}
      {val && (
        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded border text-[10px] font-medium border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 select-none">
          <span className="truncate max-w-[84px]">{valLabel(t, val)}</span>
          <button onClick={e => { e.stopPropagation(); onUpdate([]) }} className="opacity-30 hover:opacity-100 hover:text-destructive ml-auto">
            <X className="h-2 w-2" />
          </button>
        </span>
      )}
    </div>
  )
}

/* ═══ INLINE SPLITS — vertical column on right side of chart ═══ */
const InlineSplits = ({ panel, onPanelUpdate }: { panel: PanelConfig; onPanelUpdate: (p: PanelConfig) => void }) => {
  const { t } = useI18n()
  const [openDim, setOpenDim] = useState<string | null>(null)
  const [dragOverRole, setDragOverRole] = useState<DimRole | null>(null)
  const [dragOverValue, setDragOverValue] = useState(false)
  const [dragOverRemove, setDragOverRemove] = useState(false)
  const [isDraggingFromActive, setIsDraggingFromActive] = useState(false)

  const splits = Object.entries(panel.activeSplits)
  const byRole = (r: DimRole) => splits.filter(([, c]) => c.role === r)

  const handleDrop = (dimId: string, newRole: DimRole) => {
    // Don't accept value items into dimension zones
    if (VALUE_IDS.includes(dimId)) return
    const existing = panel.activeSplits[dimId]
    if (existing) {
      if (existing.role !== newRole) {
        onPanelUpdate({ ...panel, activeSplits: { ...panel.activeSplits, [dimId]: { ...existing, role: newRole } } })
      }
    } else {
      const dim = ALL_DIMS.find(d => d.id === dimId)
      if (!dim) return
      onPanelUpdate({ ...panel, activeSplits: { ...panel.activeSplits, [dimId]: { role: newRole, active: [...dim.values] } } })
    }
    setIsDraggingFromActive(false)
  }

  const handleRemove = (dimId: string) => {
    const sp = { ...panel.activeSplits }; delete sp[dimId]
    onPanelUpdate({ ...panel, activeSplits: sp })
    if (openDim === dimId) setOpenDim(null)
  }

  const handleRemoveDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverRemove(false)
    setIsDraggingFromActive(false)
    const id = e.dataTransfer.getData("text/plain")
    if (id && panel.activeSplits[id]) handleRemove(id)
  }

  return (
    <>
      <div className="w-[118px] shrink-0 border-l flex flex-col gap-1 p-1 overflow-y-auto bg-muted/20" onClick={e => e.stopPropagation()}
        onDragStart={e => {
          const id = (e as any).dataTransfer?.getData?.("text/plain") || ""
          if (id && panel.activeSplits[id]) setIsDraggingFromActive(true)
        }}
        onDragStartCapture={() => setIsDraggingFromActive(true)}
        onDragEnd={() => setIsDraggingFromActive(false)}
      >
        <ValueDropZone selectedValues={panel.selectedValues} onUpdate={vals => onPanelUpdate({ ...panel, selectedValues: vals })}
          dragOverValue={dragOverValue} setDragOverValue={setDragOverValue} />
        {(["xaxis", "filter", "split"] as DimRole[]).map(role => (
          <DropZone key={role} role={role} dims={byRole(role)} onDrop={handleDrop}
            onClickPill={id => setOpenDim(openDim === id ? null : id)}
            onRemovePill={handleRemove}
            dragOverRole={dragOverRole} setDragOverRole={setDragOverRole} />
        ))}
        {/* Remove zone — always occupies its slot, only lights up mid-drag.
            Mounting it on dragstart grew the rail while a drag was in flight,
            shoving the other three zones down under the moving cursor. */}
        <div
          aria-hidden={!isDraggingFromActive}
          className={`min-h-[22px] px-1.5 py-1 rounded border border-dashed transition-all ${
            !isDraggingFromActive ? "opacity-25 border-border pointer-events-none"
              : dragOverRemove ? "bg-red-100 dark:bg-red-900/40 border-red-400 dark:border-red-600"
              : "bg-red-50/50 dark:bg-red-950/30 border-red-200/70 dark:border-red-800/50"
          }`}
          onDragOver={e => {
            if (!e.dataTransfer.types.includes(DND_DIM)) { e.dataTransfer.dropEffect = "none"; return }
            e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverRemove(true)
          }}
          onDragLeave={e => { if (leftForGood(e)) setDragOverRemove(false) }}
          onDrop={handleRemoveDrop}
        >
          <div className={`text-[9px] font-bold uppercase tracking-wider text-center flex items-center justify-center gap-1 ${isDraggingFromActive ? "text-red-400 dark:text-red-500" : "text-muted-foreground"}`}>
            <Trash2 className="h-3 w-3 shrink-0" />
            {t("zone.remove")}
          </div>
        </div>
      </div>

      {/* Floating draggable editor */}
      {openDim && panel.activeSplits[openDim] && (
        <FloatingEditor
          dimId={openDim}
          config={panel.activeSplits[openDim]}
          onUpdate={(dimId, newCfg) => onPanelUpdate({ ...panel, activeSplits: { ...panel.activeSplits, [dimId]: newCfg } })}
          onRemove={handleRemove}
          onClose={() => setOpenDim(null)}
        />
      )}
    </>
  )
}

/**
 * Collapsible group in the dimension picker.
 *
 * The groups used to render as plain headers with every child always
 * expanded, so picking a dimension meant scrolling past all of them. Starts
 * collapsed and springs open while a search is active, so typing still
 * surfaces matches without a click.
 */
const SplitFolder = ({ groupId, label, count, forceOpen, children }: {
  groupId: string; label: string; count: number; forceOpen: boolean; children: React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const isOpen = forceOpen || open
  return (
    <Collapsible open={isOpen} onOpenChange={setOpen} key={groupId}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md hover:bg-accent text-xs font-medium text-muted-foreground cursor-pointer">
        {isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        {isOpen ? <FolderOpen className="h-3.5 w-3.5 text-amber-600 shrink-0" /> : <Folder className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
        <span className="flex-1 text-left truncate">{label}</span>
        <span className="text-[10px] tabular-nums opacity-60">{count}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  )
}

/* ═══ SIDEBAR ═══ */
const ConfigSidebar = ({ panel, onUpdate }: { panel: PanelConfig; onUpdate: (p: PanelConfig) => void }) => {
  const { t, lang } = useI18n()
  const [valSearch, setValSearch] = useState("")
  const [splitSearch, setSplitSearch] = useState("")
  const [dragOverPool, setDragOverPool] = useState(false)

  const toggleValue = (id: string) => {
    const sv = panel.selectedValues
    onUpdate({ ...panel, selectedValues: sv.includes(id) ? sv.filter(x => x !== id) : [...sv, id] })
  }
  const addSplit = (id: string) => {
    if (panel.activeSplits[id]) return
    const dim = ALL_DIMS.find(d => d.id === id)!
    // Clicking used to add the dimension as a filter with every value active
    // — a filter that excludes nothing, so the pill appeared and the chart
    // did not move. Put it on the x-axis while that shelf is free (which is
    // what a click nearly always means) and break it out as a series after.
    const hasX = Object.values(panel.activeSplits).some(c => c.role === "xaxis")
    const role: DimRole = hasX ? "split" : "xaxis"
    onUpdate({ ...panel, activeSplits: { ...panel.activeSplits, [id]: { role, active: [...dim.values] } } })
  }
  const removeSplit = (id: string) => {
    const n = { ...panel.activeSplits }; delete n[id]
    onUpdate({ ...panel, activeSplits: n })
  }
  const updateSplit = (id: string, cfg: SplitConfig) => {
    onUpdate({ ...panel, activeSplits: { ...panel.activeSplits, [id]: cfg } })
  }

  return (
    <Tabs defaultValue="values" className="flex flex-col h-full overflow-hidden">
      <TabsList className="w-full rounded-none border-b bg-transparent h-auto p-0">
        <TabsTrigger value="values" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
          {t("sidebar.valueItems")}
        </TabsTrigger>
        <TabsTrigger value="splits" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
          {t("sidebar.splitItems")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="values" className="flex-1 overflow-hidden mt-0">
        <ScrollArea className="h-full p-2">
          <Input placeholder={t("sidebar.quickFind")} value={valSearch} onChange={e => setValSearch(e.target.value)}
            className="h-7 text-xs mb-2" />
          {VALUE_TREE.map(g => <ValueFolder key={g.id} group={g} selected={panel.selectedValues} onToggle={toggleValue} search={valSearch} />)}
        </ScrollArea>
      </TabsContent>

      {/* Dropping a pill back here removes it, mirroring PivotWorkbench's
          "Available" pool. Previously a pill dragged out of the rail and
          released anywhere else just snapped back with no explanation. */}
      <TabsContent value="splits" className={`flex-1 overflow-hidden mt-0 flex flex-col transition-colors ${dragOverPool ? "bg-accent/60" : ""}`}
        onDragOver={e => {
          if (!e.dataTransfer.types.includes(DND_DIM)) { e.dataTransfer.dropEffect = "none"; return }
          e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverPool(true)
        }}
        onDragLeave={e => { if (leftForGood(e)) setDragOverPool(false) }}
        onDrop={e => {
          e.preventDefault(); setDragOverPool(false)
          const id = e.dataTransfer.getData("text/plain")
          if (id && panel.activeSplits[id]) removeSplit(id)
        }}>
        <ScrollArea className="flex-1 py-2">
          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("sidebar.addDimensions")}</div>
          <p className="px-4 pb-2 text-[10px] text-muted-foreground">{t("sidebar.addDimHelp")}</p>
          <div className="px-2 pb-2">
            <Input placeholder={t("sidebar.searchDims")} value={splitSearch} onChange={e => setSplitSearch(e.target.value)} className="h-7 text-xs" />
          </div>
          {SPLIT_TREE.map(group => {
            const vis = group.children.filter(c => !panel.activeSplits[c.id] && (!splitSearch || dimLabel(t, c.id, c.label).toLowerCase().includes(splitSearch.toLowerCase())))
            if (!vis.length) return null
            return (
              <SplitFolder key={group.id} groupId={group.id} label={groupLabel(t, group.id, group.label)}
                count={vis.length} forceOpen={!!splitSearch}>
                {vis.map(c => (
                  <UITooltip key={c.id} delayDuration={300}>
                    <TooltipTrigger asChild>
                      <div draggable
                        role="button"
                        tabIndex={0}
                        onDragStart={e => setDragPayload(e, DND_DIM, c.id)}
                        onClick={() => addSplit(c.id)}
                        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); addSplit(c.id) } }}
                        className="flex items-center justify-between w-full pl-8 pr-3 py-1.5 text-xs rounded-md hover:bg-accent cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <span>{dimLabel(t, c.id, c.label)}</span>
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <span className="text-[10px]">{c.values.length}</span>
                          <Plus className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left" align="start" className="max-w-[220px] max-h-[280px] overflow-y-auto p-0">
                      <div className="px-2.5 py-1.5 border-b bg-muted/50 sticky top-0">
                        <div className="text-[11px] font-semibold">{dimLabel(t, c.id, c.label)}</div>
                        <div className="text-[10px] text-muted-foreground">{c.values.length} {t("sidebar.items")}</div>
                      </div>
                      <div className="px-2.5 py-1.5 space-y-0.5">
                        {c.values.slice(0, 30).map(v => (
                          <div key={v} className="text-[11px] text-muted-foreground truncate">{translateDataValue(t, v, lang)}</div>
                        ))}
                        {c.values.length > 30 && (
                          <div className="text-[10px] text-muted-foreground/60 italic pt-0.5">+{c.values.length - 30} {t("sidebar.more")}</div>
                        )}
                      </div>
                    </TooltipContent>
                  </UITooltip>
                ))}
              </SplitFolder>
            )
          })}
        </ScrollArea>

        {/* Config summary */}
        <div className="flex flex-wrap gap-1 p-3 border-t bg-muted">
          {Object.entries(panel.activeSplits).map(([id, cfg]) => {
            const v = cfg.role === "xaxis" ? "default" : cfg.role === "split" ? "outline" : "secondary"
            return <Badge key={id} variant={v} className="text-[10px]">{cfg.role === "xaxis" ? "X" : cfg.role === "split" ? "S" : "F"}: {dimLabel(t, id)}</Badge>
          })}
          {panel.selectedValues.map(id => (
            <Badge key={id} variant="outline" className="text-[10px] border-purple-200 bg-purple-50 text-purple-700">V: {valLabel(t, id)}</Badge>
          ))}
        </div>
      </TabsContent>
    </Tabs>
  )
}

/* ═══ LOGIN — now uses src/pages/LoginPage.tsx ═══ */

/* ═══ ADMIN DIALOG ═══ */
const AdminDialog = ({ open, onClose, users, onSave }: {
  open: boolean; onClose: () => void; users: User[]
  onSave: (uid: string, data: { role: string; entities: string[]; years: string[]; access: string }) => void
}) => {
  const { t } = useI18n()
  const [editId, setEditId] = useState<string | null>(null)
  const [editData, setEditData] = useState<any>(null)

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shield className="h-4 w-4" /> {t("admin.title")}</DialogTitle>
          <DialogDescription>{t("admin.desc")}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="flex-1 pr-2">
          <div className="space-y-3">
            {users.map(u => {
              const editing = editId === u.id
              return (
                <div key={u.id} className={`rounded-lg border p-4 transition-colors ${editing ? "border-primary/30 bg-primary/5" : "hover:bg-muted/40"}`}>
                  {/* User header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">
                        {u.name[0]}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{u.name}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!editing && (
                        <>
                          <Badge variant={u.role === "admin" ? "destructive" : u.role === "editor" ? "default" : "secondary"} className="text-[10px]">{u.role}</Badge>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            {u.permissions.access === "edit" ? <Pencil className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                            {u.permissions.access === "edit" ? t("admin.accessEdit") : t("admin.accessView")}
                          </span>
                          <Button size="sm" variant="outline" className="h-7 text-xs ml-2" onClick={() => { setEditId(u.id); setEditData({ ...u.permissions, role: u.role }) }}>
                            <Pencil className="h-3 w-3 mr-1" />{t("admin.edit")}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* View mode — compact summary */}
                  {!editing && (
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground pl-12">
                      <span><span className="font-medium text-foreground">{t("admin.entities")}:</span> {u.permissions.entities.join(", ")}</span>
                      <span><span className="font-medium text-foreground">{t("admin.years")}:</span> {u.permissions.years.join(", ")}</span>
                    </div>
                  )}

                  {/* Edit mode */}
                  {editing && (
                    <div className="space-y-3 pl-12 pt-1">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[11px] text-muted-foreground font-medium mb-1 block">{t("admin.role")}</Label>
                          <Select value={editData.role} onValueChange={v => setEditData({ ...editData, role: v })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">{t("admin.roleAdmin")}</SelectItem>
                              <SelectItem value="editor">{t("admin.roleEditor")}</SelectItem>
                              <SelectItem value="viewer">{t("admin.roleViewer")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px] text-muted-foreground font-medium mb-1 block">{t("admin.access")}</Label>
                          <Select value={editData.access} onValueChange={v => setEditData({ ...editData, access: v })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="view">{t("admin.accessView")}</SelectItem>
                              <SelectItem value="edit">{t("admin.accessEdit")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground font-medium mb-1.5 block">{t("admin.entities")}</Label>
                        <div className="flex flex-wrap gap-2">
                          {ENTITIES.map(ent => (
                            <label key={ent} className="flex items-center gap-1.5 text-xs cursor-pointer bg-background border rounded-md px-2.5 py-1.5 hover:bg-accent">
                              <Checkbox checked={editData.entities.includes(ent)}
                                onCheckedChange={() => setEditData({ ...editData, entities: editData.entities.includes(ent) ? editData.entities.filter((x: string) => x !== ent) : [...editData.entities, ent] })} />
                              {ent}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground font-medium mb-1.5 block">{t("admin.years")}</Label>
                        <div className="flex flex-wrap gap-2">
                          {YEARS.map(yr => (
                            <label key={yr} className="flex items-center gap-1.5 text-xs cursor-pointer bg-background border rounded-md px-2.5 py-1.5 hover:bg-accent">
                              <Checkbox checked={editData.years.includes(yr)}
                                onCheckedChange={() => setEditData({ ...editData, years: editData.years.includes(yr) ? editData.years.filter((x: string) => x !== yr) : [...editData.years, yr] })} />
                              {yr}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" className="h-8 text-xs px-4" onClick={() => { onSave(editId!, editData); setEditId(null) }}>{t("admin.save")}</Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditId(null)}>{t("admin.cancel")}</Button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

/* ═══ FAQ DIALOG ═══ */
const FaqDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { t } = useI18n()
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HelpCircle className="h-4 w-4" /> {t("faq.title")}</DialogTitle>
          <DialogDescription>{t("faq.desc")}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="flex-1">
          <Accordion type="single" collapsible className="px-1">
            {FAQ_KEYS.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-sm font-medium text-left">{t(item.q)}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{t(item.a)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

/* ═══ ANALYTICS WORKBENCH ═══ */
interface WorkspaceTab { id: string; type: "dashboard" | "workspace"; title: string; panel?: PanelConfig }

interface AnalyticsWorkbenchProps {
  t: (key: string) => string
  lang: Lang
  switchLang: (l: Lang) => void
  theme: 'light' | 'dark'
  showAdmin: boolean
  onCloseAdmin: () => void
  showFaq: boolean
  onCloseFaq: () => void
  onEntitiesChange: (e: string[]) => void
  onYearsChange: (y: string[]) => void
}

export default function AnalyticsWorkbench({ t, lang, switchLang, theme, showAdmin, onCloseAdmin, showFaq, onCloseFaq, onEntitiesChange, onYearsChange }: AnalyticsWorkbenchProps) {
  const { user, setUser, canEdit } = useAuth()

  // Update palette vars synchronously during render so charts use correct colors
  PALETTE = theme === "dark" ? PALETTE_DARK : PALETTE_LIGHT
  FILL_OPACITIES = theme === "dark" ? FILL_OPACITIES_DARK : FILL_OPACITIES_LIGHT
  BOTTOM_RATIO = theme === "dark" ? BOTTOM_RATIO_DARK : BOTTOM_RATIO_LIGHT
  BAR_OPACITIES = theme === "dark" ? BAR_OPACITIES_DARK : BAR_OPACITIES_LIGHT
  BAR_BOTTOM = theme === "dark" ? BAR_BOTTOM_RATIO_DARK : BAR_BOTTOM_RATIO_LIGHT

  // A group_tag, or "ALL". Anything stored by an older build is carried over
  // rather than dropped back to the default: the toggle was per-entity, then
  // briefly used view names of its own.
  const [activeView, setActiveView] = useState<GroupView>(() => {
    const LEGACY: Record<string, string> = {
      WWF: "wwg", WWPI: "wwg", WWG: "wwg",
      WWDA: "vault", TB: "vault", VAULT: "vault",
    }
    try {
      const s = localStorage.getItem("nf_entity") || ""
      if (s === "ALL") return s
      return LEGACY[s] || (s ? s : "wwg")
    } catch {}
    return "wwg"
  })
  const [groups, setGroups] = useState<LoadedData["groups"]>([])
  const [residual, setResidual] = useState<LoadedData["residual"]>(undefined)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [users, setUsers] = useState<User[]>([])

  // The admin panel lists real accounts from fin.users. It used to be seeded
  // with four hardcoded demo logins (admin123 / coo123 / …); those were
  // removed. Non-admins get a 403 here, which is fine — the panel is
  // admin-only anyway and simply stays empty.
  useEffect(() => {
    let cancelled = false
    fetch('/api/fin/users?filter=active', { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d?.users) return
        setUsers(d.users.map((u: any) => ({
          id: u.id,
          email: u.email,
          password: '',
          name: u.name,
          role: u.role === 'ADMIN' ? 'admin' : u.role === 'READ_ONLY' ? 'viewer' : 'editor',
          permissions: { entities: [...ENTITIES], years: [...YEARS], access: u.role === 'ADMIN' ? 'edit' : 'view' },
        })))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [dataLoaded])
  const [tabs, setTabs] = useState<WorkspaceTab[]>([{ id: "dash", type: "dashboard", title: "Dashboard" }])
  const [activeTab, setActiveTab] = useState("dash")
  const [panels, setPanels] = useState<PanelConfig[]>([])
  const [activePanel, setActivePanel] = useState(0)
  const [expandedPanel, setExpandedPanel] = useState<number | null>(null)
  const [displayCurrency, setDisplayCurrency] = useState("EUR")
  const [yAxisEditPanel, setYAxisEditPanel] = useState<number | null>(null)
  const [yAxisEditWs, setYAxisEditWs] = useState(false)
  const [templates, setTemplates] = useState<SavedTemplate[]>(loadTemplates)
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [templateName, setTemplateName] = useState("")
  const [saveSource, setSaveSource] = useState<PanelConfig | null>(null)
  const [saveFolderId, setSaveFolderId] = useState<string>("custom")
  const [builtinTemplates, setBuiltinTemplates] = useState<SavedTemplate[]>([])
  const [dashboardPresets, setDashboardPresets] = useState<DashboardPreset[]>([])
  const [activePreset, setActivePreset] = useState<string>("default")
  const [activeTemplateFolder, setActiveTemplateFolder] = useState<string>("revenue_pl")
  const [pendingTemplate, setPendingTemplate] = useState<SavedTemplate | null>(null)
  const [showPanelPicker, setShowPanelPicker] = useState(false)

  // Re-slices when the group view changes. The rows themselves are fetched
  // once and cached in the loader, so this is a filter rather than a reload.
  useEffect(() => {
    setDataLoaded(false)
    _dataReady = false
    loadAllData(activeView).then(d => {
      DATA = d.DATA as any
      VALUE_TREE = d.VALUE_TREE
      SPLIT_TREE = d.SPLIT_TREE
      ALL_DIMS = d.ALL_DIMS
      DIM_FIELD = d.DIM_FIELD
      FIELD_ORDER = d.FIELD_ORDER
      YEARS = d.years
      DIM_COVERAGE = d.DIM_COVERAGE || {}
      ENTITIES = d.entities
      HAS_WALLET_BOOK = DATA.some(r => r.flowType === "Transaction Volume" || r.flowType === "Fee Revenue")
      DEFAULT_PANELS = makeGroupDefaultPanels(t)
      _dataReady = true
      setGroups(d.groups)
      // The loader falls back to everything when the stored view no longer
      // has rows; move the toggle to match, or it highlights a button that
      // is not what is on screen.
      if (activeView !== "ALL" && !d.groups.some(g => g.tag === activeView)) switchView("ALL")
      setResidual(d.residual)
      setDataLoaded(true)
      setPanels(DEFAULT_PANELS)
      setBuiltinTemplates(makeBuiltinTemplates(t))
      // Presets are rebuilt per view: top-N rankings come from the rows
      // just loaded, and a preset whose dims are absent drops itself.
      setDashboardPresets(makeDashboardPresets(t))
      setActivePreset("default")
      // Notify parent of updated entities/years for AuthContext permissions
      onEntitiesChange([...ENTITIES])
      onYearsChange([...YEARS])
      setActivePanel(0)
      setExpandedPanel(null)
    }).catch(() => {
      _dataReady = true
      setDataLoaded(true)
    })
  }, [activeView])

  /** Swap the dashboard tab over to a preset (or back to the view default). */
  const applyPreset = useCallback((id: string) => {
    const next = id === "default" ? DEFAULT_PANELS : dashboardPresets.find(p => p.id === id)?.panels
    if (!next) return
    // Deep-clone: panel titles, chart types and sort modes are editable in
    // place, and without this those edits would mutate the preset definition
    // and persist across switches.
    setPanels(next.map(p => JSON.parse(JSON.stringify(p)) as PanelConfig))
    setActivePreset(id)
    setActivePanel(0)
    setExpandedPanel(null)
  }, [dashboardPresets])

  const switchView = (v: GroupView) => {
    setActiveView(v)
    try { localStorage.setItem("nf_entity", v) } catch {}
  }

  // All hooks MUST be above the early returns (React Rules of Hooks)
  const updatePanel = useCallback((updated: PanelConfig) => {
    setPanels(prev => prev.map((p, i) => i === activePanel ? updated : p))
  }, [activePanel])

  // Live rates from the same source as the rest of the app — /api/fin/fx-rates,
  // backed by staging.fx_rates. Workbench values are stored in EUR, so the
  // rate is EUR → the selected display currency. Previously this read a
  // hardcoded { EUR: 1, USD: 1.08 } map, which drifted ~6% from the real rate
  // and offered no other currency.
  const { convertBetween, availableCurrencies, ratesLoaded } = useCurrency()
  const fxRate = useMemo(
    () => (displayCurrency === "EUR" ? 1 : convertBetween(1, "EUR", displayCurrency)),
    [convertBetween, displayCurrency]
  )
  const DISPLAY_CURRENCIES = availableCurrencies
  const chartData = useMemo(() => panels.map(p => computeData(p, user?.permissions, fxRate, t, lang)), [panels, user, fxRate, t, lang])

  // KPI summary metrics — computed from loaded data (must be before early returns per Rules of Hooks)
  const kpiMetrics = useMemo(() => {
    if (!DATA.length) return []
    const latestYear = YEARS[YEARS.length - 1] || "2025"
    const prevYear = YEARS.length > 1 ? YEARS[YEARS.length - 2] : null

    const sumBy = (filter: (d: DataRow) => boolean) => DATA.filter(filter).reduce((s, d) => s + d.value, 0)
    const pctStr = (curr: number, prev: number) => prev ? ((curr - prev) / Math.abs(prev) * 100).toFixed(1) + "%" : null

    // Build monthly sparkline data for a metric
    const monthlySparkline = (filter: (d: DataRow) => boolean) =>
      MONTHS.map(m => DATA.filter(d => d.year === latestYear && d.month === m && filter(d)).reduce((s, d) => s + d.value, 0))
    // Build yearly sparkline
    const yearlySparkline = (filter: (d: DataRow) => boolean) =>
      YEARS.map(y => DATA.filter(d => d.year === y && filter(d)).reduce((s, d) => s + d.value, 0))

    // A view with a wallet book leads with volume and fees; one without leads
    // with the P&L. Read from the rows, because "The Vault" is both a wallet
    // business and a GL and the button name no longer answers this.
    if (HAS_WALLET_BOOK) {
      const txCurrent = sumBy(d => d.year === latestYear && d.flowType === "Transaction Volume")
      const txPrev = prevYear ? sumBy(d => d.year === prevYear && d.flowType === "Transaction Volume") : 0
      const feeCurrent = sumBy(d => d.year === latestYear && d.flowType === "Fee Revenue")
      const feePrev = prevYear ? sumBy(d => d.year === prevYear && d.flowType === "Fee Revenue") : 0
      const balCurrent = sumBy(d => d.year === latestYear && d.flowType === "Balance")
      const clientCount = new Set(DATA.filter(d => d.year === latestYear && d.clientName !== "(unknown)" && d.clientName !== "(none)").map(d => d.clientName)).size

      return [
        { label: activeView === "ALL" ? t("kpi.totalVolume") : t("kpi.transactionVolume"), value: fmt(txCurrent), change: pctStr(txCurrent, txPrev), positive: txCurrent >= txPrev, icon: Activity, sparkline: monthlySparkline(d => d.flowType === "Transaction Volume"), subtitle: `${t("kpi.vs")} ${fmt(txPrev)} ${t("kpi.lastYear")}` },
        { label: t("kpi.feeRevenue"), value: fmt(feeCurrent), change: pctStr(feeCurrent, feePrev), positive: feeCurrent >= feePrev, icon: DollarSign, sparkline: monthlySparkline(d => d.flowType === "Fee Revenue"), subtitle: `${t("kpi.vs")} ${fmt(feePrev)} ${t("kpi.lastYear")}` },
        { label: t("kpi.aumBalance"), value: fmt(balCurrent), change: null, positive: true, icon: Briefcase, sparkline: yearlySparkline(d => d.flowType === "Balance"), subtitle: latestYear },
        { label: t("kpi.activeClients"), value: clientCount.toLocaleString(), change: null, positive: true, icon: Users, sparkline: [], subtitle: `${latestYear} ${t("kpi.unique")}` },
      ]
    }

    // No wallet book — lead with the P&L.
    const revCurrent = sumBy(d => d.year === latestYear && d.dataSource === "revenue")
    const revPrev = prevYear ? sumBy(d => d.year === prevYear && d.dataSource === "revenue") : 0
    const costCurrent = Math.abs(sumBy(d => d.year === latestYear && d.dataSource === "costs"))
    const costPrev = prevYear ? Math.abs(sumBy(d => d.year === prevYear && d.dataSource === "costs")) : 0
    const aumCurrent = sumBy(d => d.year === latestYear && d.flowType === "Assets Held")
    const marginPct = revCurrent > 0 ? ((revCurrent - costCurrent) / revCurrent * 100) : 0
    const prevMarginPct = revPrev > 0 ? ((revPrev - costPrev) / revPrev * 100) : 0
    const clientCount = new Set(DATA.filter(d => d.year === latestYear && d.clientName !== "(unknown)" && d.clientName !== "(none)").map(d => d.clientName)).size

    return [
      { label: t("kpi.totalRevenue"), value: fmt(revCurrent), change: pctStr(revCurrent, revPrev), positive: revCurrent >= revPrev, icon: DollarSign, sparkline: monthlySparkline(d => d.dataSource === "revenue"), subtitle: `${t("kpi.vs")} ${fmt(revPrev)} ${t("kpi.in")} ${prevYear || "N/A"}` },
      { label: t("kpi.totalExpenses"), value: fmt(costCurrent), change: pctStr(costCurrent, costPrev), positive: costCurrent <= costPrev, icon: Activity, sparkline: monthlySparkline(d => d.dataSource === "costs").map(Math.abs), subtitle: `${t("kpi.vs")} ${fmt(costPrev)} ${t("kpi.in")} ${prevYear || "N/A"}` },
      { label: t("kpi.operatingMargin"), value: `${marginPct.toFixed(1)}%`, change: prevMarginPct ? `${(marginPct - prevMarginPct).toFixed(1)}pp` : null, positive: marginPct >= prevMarginPct, icon: Target, sparkline: YEARS.map(y => { const r = sumBy(d => d.year === y && d.dataSource === "revenue"); const c = Math.abs(sumBy(d => d.year === y && d.dataSource === "costs")); return r > 0 ? (r - c) / r * 100 : 0 }), subtitle: t("kpi.revenueMinusCosts") },
      { label: t("kpi.aumAuc"), value: fmt(aumCurrent), change: null, positive: true, icon: Briefcase, sparkline: yearlySparkline(d => d.flowType === "Assets Held"), subtitle: `${clientCount} ${t("kpi.activeClientsSub")}` },
    ]
  }, [panels, DATA.length, activeView])

  // Must be before early return to satisfy Rules of Hooks (constant hook count)
  const i18nValue = useMemo(() => ({ lang, t }), [lang, t])

  if (!dataLoaded) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-6">
        <div className="relative">
          <img src="/logo.svg" alt="Logo" className="h-12 mx-auto logo-themed" />
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-0.5 rounded-full loading-shimmer" />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-lg font-semibold tracking-tight">{t("app.loading")}</h2>
          <p className="text-sm text-muted-foreground">{t("app.loadingSub")}</p>
        </div>
        <div className="flex justify-center gap-1">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-pulse" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      </div>
    </div>
  )

  const updateUser = (uid: string, data: any) => {
    setUsers(prev => prev.map(u => u.id === uid ? { ...u, role: data.role, permissions: { entities: data.entities, years: data.years, access: data.access } } : u))
  }

  // Workspace management
  const addWorkspace = () => {
    const id = `ws_${Date.now()}`
    const firstVal = VALUE_TREE[0]?.children[0]?.id ? [VALUE_TREE[0].children[0].id] : []
    const panel = makePanel(t("tab.newAnalysis"), firstVal, { d_month: { role: "xaxis", active: [...MONTHS] }, d_year: { role: "filter", active: YEARS.slice(-1) } })
    setTabs(prev => [...prev, { id, type: "workspace", title: `${t("tab.analysis")} ${prev.length}`, panel }])
    setActiveTab(id)
  }
  /**
   * Take an existing dashboard panel into a dedicated workspace tab.
   *
   * Deep-clones the panel so editing the workspace copy never mutates the
   * dashboard source, opens it as a new workspace tab (inherits the full
   * ConfigSidebar + workspace layout that already exists for blank
   * workspaces), and activates the new tab. This is the "open chart in a
   * dedicated workspace to work deeper on it" feature — it intentionally
   * reuses the existing analytics dataset rather than routing to
   * PivotWorkbench which runs on a different data source.
   */
  const openPanelInWorkspace = (source: PanelConfig) => {
    const id = `ws_${Date.now()}`
    const panel: PanelConfig = JSON.parse(JSON.stringify(source))
    setTabs(prev => [...prev, { id, type: "workspace" as const, title: source.title, panel }])
    setActiveTab(id)
  }
  const closeTab = (id: string) => {
    setTabs(prev => prev.filter(t => t.id !== id))
    if (activeTab === id) setActiveTab("dash")
  }
  const updateWorkspacePanel = (tabId: string, p: PanelConfig) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, panel: p } : t))
  }
  const renameTab = (tabId: string, title: string) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, title } : t))
  }

  // Template functions
  const promptSaveTemplate = (panel: PanelConfig) => {
    setSaveSource(panel)
    setTemplateName(panel.title)
    setShowSaveTemplate(true)
  }
  const confirmSaveTemplate = () => {
    if (!saveSource || !templateName.trim()) return
    const t: SavedTemplate = {
      id: `tpl_${Date.now()}`,
      name: templateName.trim(),
      folder: saveFolderId,
      panel: JSON.parse(JSON.stringify(saveSource)),
      createdAt: new Date().toISOString(),
    }
    const updated = [...templates, t]
    setTemplates(updated)
    saveTemplates(updated)
    setShowSaveTemplate(false)
    setTemplateName("")
    setSaveSource(null)
    setSaveFolderId("custom")
  }
  const deleteTemplate = (id: string) => {
    const updated = templates.filter(t => t.id !== id)
    setTemplates(updated)
    saveTemplates(updated)
  }
  const loadTemplate = (tpl: SavedTemplate) => {
    if (activeTab === "dash") {
      // On dashboard: prompt user to pick which panel slot to replace
      setPendingTemplate(tpl)
      setShowTemplates(false)
      setShowPanelPicker(true)
    } else {
      // On workspace: open as new workspace tab (original behavior)
      const id = `ws_${Date.now()}`
      const panel = JSON.parse(JSON.stringify(tpl.panel))
      setTabs(prev => [...prev, { id, type: "workspace" as const, title: tpl.name, panel }])
      setActiveTab(id)
      setShowTemplates(false)
    }
  }
  const replacePanelWithTemplate = (slotIndex: number) => {
    if (!pendingTemplate) return
    const panel = JSON.parse(JSON.stringify(pendingTemplate.panel))
    setPanels(prev => {
      const n = [...prev]
      n[slotIndex] = panel
      return n
    })
    setActivePanel(slotIndex)
    setPendingTemplate(null)
    setShowPanelPicker(false)
  }

  const currentTab = tabs.find(tb => tb.id === activeTab)
  const wsPanel = currentTab?.type === "workspace" ? currentTab.panel! : null
  const wsData = wsPanel ? computeData(wsPanel, user?.permissions, fxRate, t, lang) : null
  // Every panel is shown. This used to be `.slice(0, 4)`, which silently
  // dropped panels 5+ — a preset with five panels rendered as four with no
  // indication anything was missing.
  const visiblePanels = expandedPanel !== null ? [expandedPanel] : panels.map((_, i) => i)
  // 1 column expanded, 2 up to four panels, 3 beyond that — keeps a 6-panel
  // dashboard on one screen instead of a long scroll of half-width charts.
  const gridCols = expandedPanel !== null ? 1 : visiblePanels.length <= 4 ? 2 : 3

  return (
    <I18nContext.Provider value={i18nValue}>
            {/* ANALYTICS TAB BAR — inside content area */}
            <ErrorBoundary fallbackMessage="Tab bar crashed">
            <div className="flex items-center gap-0.5 px-3 border-b bg-muted/50 shrink-0 overflow-x-auto">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id ? "border-primary text-foreground bg-background" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}>
                  {tab.type === "dashboard" ? <LayoutGrid className="h-3.5 w-3.5" /> : <Sliders className="h-3.5 w-3.5" />}
                  {tab.type === "workspace" && activeTab === tab.id ? (
                    <input value={tab.title} onClick={e => e.stopPropagation()} onChange={e => renameTab(tab.id, e.target.value)}
                      className="bg-transparent border-none text-xs font-medium outline-none w-[80px]" />
                  ) : <span>{tab.type === "dashboard" ? t("tab.dashboard") : tab.title}</span>}
                  {tab.type === "workspace" && (
                    <button onClick={e => { e.stopPropagation(); closeTab(tab.id) }}
                      className="ml-1 rounded p-0.5 hover:bg-destructive/10 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </button>
              ))}
              <button onClick={addWorkspace} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary hover:text-primary/80 whitespace-nowrap">
                <PlusCircle className="h-3.5 w-3.5" />{t("tab.newWorkspace")}
              </button>
            </div>

            </ErrorBoundary>
            {/* ANALYTICS CONTROLS BAR */}
            <ErrorBoundary fallbackMessage="Controls bar crashed">
            <div className="flex items-center justify-between px-4 py-2 border-b shrink-0 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {/* Group view — WWG / The Vault / All. A single company is
                    still reachable: drop the Entity dimension on any shelf. */}
                <ToggleGroup type="single" value={activeView} onValueChange={v => { if (v) switchView(v) }} size="sm" className="border rounded-md">
                  {groups.map(g => (
                    <ToggleGroupItem key={g.tag} value={g.tag} className="h-7 px-2.5 text-xs font-medium">
                      {g.label}
                    </ToggleGroupItem>
                  ))}
                  <ToggleGroupItem value="ALL" className="h-7 px-2.5 text-xs font-medium">{t("entity.all")}</ToggleGroupItem>
                </ToggleGroup>
                {residual && residual.rows > 0 && (
                  <UITooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-300/70 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-[10px] cursor-default">
                        <AlertTriangle className="h-3 w-3" />
                        {residual.rows.toLocaleString()}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <div className="text-[11px] font-medium mb-1">{t("view.residualTitle")}</div>
                      <div className="text-[11px] text-muted-foreground mb-1.5">
                        {t("view.residualBody")
                          .replace("{rows}", residual.rows.toLocaleString())
                          .replace("{value}", `€${fmt(residual.value)}`)}
                      </div>
                      {residual.bySource.map(s => (
                        <div key={s.source} className="text-[10px] flex justify-between gap-3">
                          <span>{translateDataValue(t, s.source, lang)}</span>
                          <span className="tabular-nums">{`€${fmt(s.value)}`}</span>
                        </div>
                      ))}
                      <div className="text-[10px] mt-1.5 pt-1.5 border-t border-border/60 text-muted-foreground">
                        {t("view.residualFix")}
                        {residual.externalRows > 0 && ` ${t("view.residualExternal")
                          .replace("{rows}", residual.externalRows.toLocaleString())
                          .replace("{value}", `€${fmt(residual.externalValue)}`)}`}
                      </div>
                    </TooltipContent>
                  </UITooltip>
                )}
                <Separator orientation="vertical" className="h-5" />
                {/* Language toggle */}
                <ToggleGroup type="single" value={lang} onValueChange={v => { if (v) switchLang(v as Lang) }} size="sm" className="border rounded-md">
                  {LANGUAGES.map(l => (
                    <ToggleGroupItem key={l.id} value={l.id} className="h-7 px-2.5 text-xs font-medium">
                      {l.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                {/* Currency toggle */}
                <ToggleGroup type="single" value={displayCurrency} onValueChange={v => { if (v) setDisplayCurrency(v) }} size="sm" className="border rounded-md">
                  {DISPLAY_CURRENCIES.map(c => (
                    <ToggleGroupItem key={c} value={c} className="h-7 px-2.5 text-xs font-medium">
                      {c}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
              <div className="flex items-center gap-2">
                {/* Dashboard preset picker — pre-built multi-panel views */}
                {activeTab === "dash" && dashboardPresets.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1.5">
                        <LayoutGrid className="h-3.5 w-3.5" />
                        {t(dashboardPresets.find(p => p.id === activePreset)?.nameKey || "preset.default")}
                        <ChevronDown className="h-3 w-3 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {t("preset.menuLabel")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => applyPreset("default")}>
                        <LayoutGrid className="h-4 w-4" />{t("preset.default")}
                        <Badge variant="secondary" className="ml-auto text-[9px] h-4 px-1">{DEFAULT_PANELS.length}</Badge>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {dashboardPresets.map(ps => (
                        <DropdownMenuItem key={ps.id} onClick={() => applyPreset(ps.id)}>
                          <ps.Icon className="h-4 w-4" />{t(ps.nameKey)}
                          <Badge variant="secondary" className="ml-auto text-[9px] h-4 px-1">{ps.panels.length}</Badge>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowTemplates(true)}>
                  <BookOpen className="h-3.5 w-3.5" />{t("topbar.templates")}
                  {templates.length > 0 && <Badge variant="secondary" className="text-[9px] h-4 px-1">{templates.length}</Badge>}
                </Button>
                {/* Export */}
                {activeTab === "dash" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm"><Download className="h-3.5 w-3.5" />{t("topbar.export")}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { const d = chartData[activePanel]; exportCSV(d.data, d.series, d.xKey, panels[activePanel].title.replace(/\s+/g, "_")) }}>
                        <FileSpreadsheet className="h-4 w-4" />{t("topbar.panelCsv")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportAllCSV(panels, chartData)}>
                        <FileSpreadsheet className="h-4 w-4" />{t("topbar.allCsv")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => exportPDFWithCharts(panels, panels.map((_, i) => `chart-${i}`), t)}>
                        <FileDown className="h-4 w-4" />{t("topbar.exportPdf")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {activeTab !== "dash" && wsData && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm"><Download className="h-3.5 w-3.5" />{t("topbar.export")}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => exportCSV(wsData.data, wsData.series, wsData.xKey, currentTab!.title.replace(/\s+/g, "_"))}>
                        <FileSpreadsheet className="h-4 w-4" />{t("topbar.csv")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => exportPDFWithCharts([wsPanel!], [`ws-chart-${activeTab}`], t)}>
                        <FileDown className="h-4 w-4" />{t("topbar.exportPdf")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            </ErrorBoundary>
            {/* ═══ ANALYTICS CONTENT ═══ */}
            <div className="flex-1 flex overflow-hidden">
              {activeTab === "dash" ? (
            <>
              {/* KPI Summary Cards + Dashboard panels */}
              <div className="flex-1 flex flex-col gap-3 p-3 overflow-auto bg-muted/30">
              {/* KPI Row — enhanced with sparklines and richer context */}
              <ErrorBoundary fallbackMessage="KPI cards crashed">
              {kpiMetrics.length > 0 && expandedPanel === null && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0">
                  {kpiMetrics.map((kpi, i) => {
                    const IconComp = kpi.icon
                    return (
                      <Card key={i} className="gap-0 py-0 overflow-hidden group hover:shadow-md transition-shadow">
                        <CardHeader className="pb-0 pt-3 px-4 gap-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <div className="h-6 w-6 rounded-md bg-primary/8 dark:bg-primary/15 flex items-center justify-center">
                                <IconComp className="h-3 w-3 text-primary" />
                              </div>
                              <CardDescription className="text-[11px] font-medium">{kpi.label}</CardDescription>
                            </div>
                            {kpi.change && (
                              <Badge variant="outline" className={`text-[10px] gap-0.5 tabular-nums font-semibold ${kpi.positive ? "text-emerald-600 border-emerald-200 bg-emerald-50 dark:text-emerald-400 dark:border-emerald-800 dark:bg-emerald-950" : "text-red-600 border-red-200 bg-red-50 dark:text-red-400 dark:border-red-800 dark:bg-red-950"}`}>
                                {kpi.positive ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                                {kpi.positive ? "+" : ""}{kpi.change}
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1.5">
                            <CardTitle className="text-2xl font-bold tabular-nums tracking-tight">{kpi.value}</CardTitle>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{kpi.subtitle}</p>
                          </div>
                        </CardHeader>
                        <div className="h-8 px-4 mt-1 mb-1">
                          {kpi.sparkline && kpi.sparkline.length > 1 && (
                            <Sparkline data={kpi.sparkline} positive={kpi.positive} width={200} height={28} />
                          )}
                        </div>
                      </Card>
                    )
                  })}
                </div>
              )}
              </ErrorBoundary>
              {/* Natural Language Query Bar */}
              <ErrorBoundary fallbackMessage="Query bar crashed">
              {expandedPanel === null && (
                <NLQueryBar onQuery={(p) => {
                  // Replace the first panel or add a new one
                  setPanels(prev => [p, ...prev.slice(0, 5)])
                  setActivePanel(0)
                }} />
              )}
              </ErrorBoundary>
              {/* Chart Grid — auto-adapts: 1 col for expanded, 2 for ≤4, 3 for 5+ */}
              <ErrorBoundary fallbackMessage="Chart panels crashed">
              <div className="flex-1 grid gap-3"
                style={{
                  gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
                  gridAutoRows: expandedPanel !== null ? "1fr" : "minmax(280px, 1fr)",
                }}>
                {visiblePanels.map(idx => {
                  const p = panels[idx]
                  const cd = chartData[idx]
                  const isActive = idx === activePanel
                  return (
                    <Card key={idx} className={`flex flex-col cursor-pointer overflow-hidden ${isActive ? "ring-2 ring-ring" : ""}`}
                      onClick={() => setActivePanel(idx)}>
                      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
                        <input value={p.title} onClick={e => e.stopPropagation()}
                          onChange={e => { if (!canEdit) return; const n = [...panels]; n[idx] = { ...n[idx], title: e.target.value }; setPanels(n) }}
                          readOnly={!canEdit}
                          className="flex-1 bg-transparent border-none text-sm font-semibold outline-none min-w-0" />
                        <ChartTypePicker value={p.chartType} disabled={!canEdit}
                          onChange={v => { const n = [...panels]; n[idx] = { ...n[idx], chartType: v }; setPanels(n) }} />
                        {/* Sort dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => e.stopPropagation()}>
                              {p.sortMode === "desc" ? <ArrowDown className="h-3.5 w-3.5" /> : p.sortMode === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">{t("sort.title")}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { const n = [...panels]; n[idx] = { ...n[idx], sortMode: "none" }; setPanels(n) }}>
                              <ArrowUpDown className="h-3.5 w-3.5" />{t("sort.default")} {p.sortMode === "none" || !p.sortMode ? "✓" : ""}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { const n = [...panels]; n[idx] = { ...n[idx], sortMode: "desc" }; setPanels(n) }}>
                              <ArrowDown className="h-3.5 w-3.5" />{t("sort.desc")} {p.sortMode === "desc" ? "✓" : ""}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { const n = [...panels]; n[idx] = { ...n[idx], sortMode: "asc" }; setPanels(n) }}>
                              <ArrowUp className="h-3.5 w-3.5" />{t("sort.asc")} {p.sortMode === "asc" ? "✓" : ""}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { const n = [...panels]; n[idx] = { ...n[idx], sortMode: "alpha_asc" }; setPanels(n) }}>
                              <ArrowDownAZ className="h-3.5 w-3.5" />{t("sort.alphaAsc")} {p.sortMode === "alpha_asc" ? "✓" : ""}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { const n = [...panels]; n[idx] = { ...n[idx], sortMode: "alpha_desc" }; setPanels(n) }}>
                              <ArrowUpAZ className="h-3.5 w-3.5" />{t("sort.alphaDesc")} {p.sortMode === "alpha_desc" ? "✓" : ""}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <UITooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6"
                            onClick={e => { e.stopPropagation(); setYAxisEditPanel(yAxisEditPanel === idx ? null : idx) }}>
                            <Settings2 className={`h-3 w-3 ${yAxisEditPanel === idx ? "text-primary" : "text-muted-foreground"}`} />
                          </Button>
                        </TooltipTrigger><TooltipContent side="bottom" className="text-xs">{t("yaxis.tooltip")}</TooltipContent></UITooltip>
                        <UITooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6"
                            onClick={e => { e.stopPropagation(); promptSaveTemplate(p) }}>
                            <Save className="h-3 w-3 text-muted-foreground" />
                          </Button>
                        </TooltipTrigger><TooltipContent side="bottom" className="text-xs">{t("tpl.saveTooltip")}</TooltipContent></UITooltip>
                        <UITooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6"
                            onClick={e => { e.stopPropagation(); openPanelInWorkspace(p) }}>
                            <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
                          </Button>
                        </TooltipTrigger><TooltipContent side="bottom" className="text-xs">{t("tab.openInWorkspace")}</TooltipContent></UITooltip>
                        <Button variant="ghost" size="icon" className="h-6 w-6"
                          onClick={e => { e.stopPropagation(); setExpandedPanel(expandedPanel === idx ? null : idx) }}>
                          {expandedPanel === idx ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
                        </Button>
                      </div>
                      <div className="flex-1 flex min-h-0 overflow-hidden">
                        <div className="flex-1 p-1.5 min-h-0 overflow-hidden" id={`chart-${idx}`}>
                          <ChartRenderer type={p.chartType} data={cd.data} series={cd.series} xKey={cd.xKey} id={`chart-inner-${idx}`}
                            yAxis={p.yAxis} panel={p} />
                        </div>
                        <InlineSplits panel={p} onPanelUpdate={(updated) => { const n = [...panels]; n[idx] = updated; setPanels(n) }} />
                      </div>
                    </Card>
                  )
                })}
              </div>
              </ErrorBoundary>
              </div>

              {/* Y-Axis editor for dashboard panels */}
              {yAxisEditPanel !== null && panels[yAxisEditPanel] && (
                <YAxisEditor
                  config={panels[yAxisEditPanel].yAxis || {}}
                  onChange={c => { const n = [...panels]; n[yAxisEditPanel] = { ...n[yAxisEditPanel], yAxis: c }; setPanels(n) }}
                  onClose={() => setYAxisEditPanel(null)}
                />
              )}

              {/* Config sidebar removed from dashboard — available only in workspaces */}
            </>
          ) : wsPanel && wsData && (
            <>
              {/* Workspace: single full panel */}
              <div className="flex-1 p-3 overflow-hidden bg-muted/30">
                <Card className="h-full flex flex-col">
                  <div className="flex items-center gap-3 px-4 py-2.5 border-b">
                    <input value={currentTab!.title} onChange={e => renameTab(currentTab!.id, e.target.value)}
                      className="flex-1 bg-transparent border-none text-base font-semibold outline-none" />
                    <ChartTypePicker value={wsPanel.chartType}
                      onChange={v => updateWorkspacePanel(activeTab, { ...wsPanel, chartType: v })} />
                    <UITooltip><TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => promptSaveTemplate(wsPanel)}>
                        <Save className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger><TooltipContent side="bottom" className="text-xs">{t("tpl.saveTooltip")}</TooltipContent></UITooltip>
                  </div>
                  <div className="flex-1 flex min-h-0 overflow-hidden">
                    <div className="flex-1 p-2 min-h-0 overflow-hidden" id={`ws-chart-${activeTab}`}>
                      <ChartRenderer type={wsPanel.chartType} data={wsData.data} series={wsData.series} xKey={wsData.xKey} id={`ws-inner-${activeTab}`}
                        yAxis={wsPanel.yAxis} panel={wsPanel} onYAxisClick={() => setYAxisEditWs(!yAxisEditWs)} />
                    </div>
                    <InlineSplits panel={wsPanel} onPanelUpdate={(updated) => updateWorkspacePanel(activeTab, updated)} />
                  </div>
                </Card>
              </div>
              {/* Y-Axis editor for workspace */}
              {yAxisEditWs && wsPanel && (
                <YAxisEditor
                  config={wsPanel.yAxis || {}}
                  onChange={c => updateWorkspacePanel(activeTab, { ...wsPanel, yAxis: c })}
                  onClose={() => setYAxisEditWs(false)}
                />
              )}
              {/* Workspace sidebar */}
              <div className="w-[290px] border-l shrink-0 flex flex-col overflow-hidden">
                <div className="px-3 py-2.5 border-b flex items-center gap-2 shrink-0">
                  <Sliders className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-semibold">{t("sidebar.wsConfig")}</span>
                </div>
                <ConfigSidebar panel={wsPanel} onUpdate={p => updateWorkspacePanel(activeTab, p)} />
              </div>
            </>
          )}
            </div>

        {/* Modals */}
        <ErrorBoundary fallbackMessage="Dialogs crashed">
        <AdminDialog open={showAdmin} onClose={onCloseAdmin} users={users} onSave={updateUser} />
        <FaqDialog open={showFaq} onClose={onCloseFaq} />

        {/* Save Template Dialog */}
        <Dialog open={showSaveTemplate} onOpenChange={v => { if (!v) { setShowSaveTemplate(false); setSaveSource(null) } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Save className="h-4 w-4" /> {t("tpl.saveTitle")}</DialogTitle>
              <DialogDescription>{t("tpl.saveDesc")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="tpl-name">{t("tpl.name")}</Label>
                <Input id="tpl-name" value={templateName} onChange={e => setTemplateName(e.target.value)}
                  placeholder={t("tpl.placeholder")}
                  onKeyDown={e => { if (e.key === "Enter") confirmSaveTemplate() }}
                  autoFocus />
              </div>
              <div className="space-y-1.5">
                <Label>{t("folder.folderLabel")}</Label>
                <Select value={saveFolderId} onValueChange={setSaveFolderId}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_FOLDERS.map(f => (
                      <SelectItem key={f.id} value={f.id} className="text-xs">{t(f.i18nKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {saveSource && (
                <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground space-y-1">
                  <div><span className="font-medium text-foreground">{t("tpl.chartType")}</span> {saveSource.chartType}</div>
                  <div><span className="font-medium text-foreground">{t("tpl.values")}</span> {saveSource.selectedValues.map(v => valLabel(t, v)).join(", ") || "—"}</div>
                  <div><span className="font-medium text-foreground">{t("tpl.dimensions")}</span> {Object.keys(saveSource.activeSplits).length} {t("tpl.active")}</div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setShowSaveTemplate(false); setSaveSource(null) }}>{t("tpl.cancel")}</Button>
                <Button size="sm" onClick={confirmSaveTemplate} disabled={!templateName.trim()}>{t("tpl.save")}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Templates Browser Dialog — folder-based navigation */}
        <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><BookOpen className="h-4 w-4" /> {t("tpl.browseTitle")}</DialogTitle>
              <DialogDescription>{t("tpl.browseDescFull")}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-1 min-h-0 gap-0 border rounded-lg overflow-hidden">
              {/* Folder sidebar */}
              <div className="w-48 border-r bg-muted/30 shrink-0 flex flex-col">
                <div className="px-3 py-2 border-b">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("folder.label")}</span>
                </div>
                <ScrollArea className="flex-1">
                  <div className="p-1.5 space-y-0.5">
                    {TEMPLATE_FOLDERS.map(folder => {
                      const FIcon = folder.icon
                      const count = folder.id === "custom"
                        ? templates.filter(t => (t.folder || "custom") === "custom").length
                        : builtinTemplates.filter(t => t.folder === folder.id).length + templates.filter(t => t.folder === folder.id).length
                      return (
                        <button key={folder.id}
                          onClick={() => setActiveTemplateFolder(folder.id)}
                          className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors text-left ${activeTemplateFolder === folder.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                          <FIcon className="h-3.5 w-3.5 shrink-0" />
                          <span className="flex-1 truncate">{t(folder.i18nKey)}</span>
                          {count > 0 && <span className="text-[10px] tabular-nums opacity-60">{count}</span>}
                        </button>
                      )
                    })}
                  </div>
                </ScrollArea>
              </div>
              {/* Template list for active folder */}
              <div className="flex-1 flex flex-col min-w-0">
                <div className="px-3 py-2 border-b flex items-center gap-2">
                  <span className="text-xs font-semibold">{t(TEMPLATE_FOLDERS.find(f => f.id === activeTemplateFolder)?.i18nKey || "folder.custom")}</span>
                </div>
                <ScrollArea className="flex-1">
                  {(() => {
                    const folderBuiltin = builtinTemplates.filter(t => t.folder === activeTemplateFolder)
                    const folderCustom = templates.filter(t => (t.folder || "custom") === activeTemplateFolder)
                    const allInFolder = [...folderBuiltin, ...folderCustom]
                    if (allInFolder.length === 0) {
                      return (
                        <div className="text-center py-12 text-muted-foreground">
                          <FolderOpen className="h-8 w-8 mx-auto mb-3 opacity-40" />
                          <p className="text-sm">{activeTemplateFolder === "custom" ? t("tpl.noCustom") : t("tpl.noTemplates")}</p>
                          <p className="text-xs mt-1">{activeTemplateFolder === "custom" ? t("tpl.noCustomHint") : ""}</p>
                        </div>
                      )
                    }
                    return (
                      <div className="p-2 space-y-1.5">
                        {allInFolder.map(tpl => {
                          const isBuiltin = tpl.id.startsWith("builtin_")
                          return (
                            <div key={tpl.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors group">
                              <button onClick={() => loadTemplate(tpl)} className="flex-1 text-left min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium truncate">{tpl.name}</span>
                                  {isBuiltin && <Badge variant="secondary" className="text-[9px] h-4 px-1.5 shrink-0">{t("tpl.builtin")}</Badge>}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5 flex gap-3">
                                  <span>{tpl.panel.chartType}</span>
                                  <span>{tpl.panel.selectedValues.map(v => valLabel(t, v)).join(", ")}</span>
                                  <span>{Object.keys(tpl.panel.activeSplits).length} {t("misc.dims")}</span>
                                </div>
                              </button>
                              {!isBuiltin && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => deleteTemplate(tpl.id)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })()}
                </ScrollArea>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Panel Picker Dialog — choose which dashboard slot to replace */}
        <Dialog open={showPanelPicker} onOpenChange={v => { if (!v) { setShowPanelPicker(false); setPendingTemplate(null) } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> {t("picker.title")}</DialogTitle>
              <DialogDescription>
                {t("picker.loading")} <span className="font-medium text-foreground">{pendingTemplate?.name}</span> — {t("picker.desc")}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 pt-2">
              {visiblePanels.map(idx => (
                <button key={idx}
                  onClick={() => replacePanelWithTemplate(idx)}
                  className="p-3 rounded-lg border-2 hover:border-primary hover:bg-accent/50 transition-colors text-left group">
                  <div className="text-xs font-medium truncate">{panels[idx]?.title}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{panels[idx]?.chartType} — {t("picker.slot")} {idx + 1}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => { setShowPanelPicker(false); setPendingTemplate(null) }}>{t("misc.cancel")}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </ErrorBoundary>
    </I18nContext.Provider>
  )
}
